import { FC, memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import DOMPurify from 'dompurify';
import parse from 'html-react-parser';
import { useTranslation } from 'react-i18next';
import { showSnackbar } from '../../../../stores/redux/actions';
import { enumSnackbarStatusType } from '../../../../types/enums';
import { useDispatch } from 'react-redux';
import {
    Avatar,
    Card,
    CardActions,
    Box,
    CardContent,
    CardHeader,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    FormControl,
    Grid,
    IconButton,
    InputLabel,
    MenuItem,
    OutlinedInput,
    Paper,
    Stack,
    Select,
    Tab,
    Tabs,
    Typography,
    TextField,
    Checkbox,
    ListItemText,
    Switch,
    FormHelperText,
    Menu,
    ListItemIcon,
    InputAdornment,
    Button,
    DialogContentText,
    Tooltip,
    ListSubheader,
    Chip,
} from '@mui/material';
import {
    AddCircle,
    Close,
    CloudUploadOutlined,
    Delete,
    DragIndicator,
    MoreHoriz,
    LocalAtm,
    Refresh,
    Edit,
    Done,
    DeleteOutlined,
    Info,
    CloudDownload,
    HealingOutlined,
    CachedOutlined,
    FileUpload,
    EditTwoTone,
    DeleteOutlineTwoTone,
} from '@mui/icons-material';
import { blue } from '@mui/material/colors';
import { styled } from '@mui/material/styles';
import { SCUIButton } from '../../../../atomic/atoms';
import { useDropzone } from 'react-dropzone';
import { CampaignManagementCommand } from '../../../../services/command';
import { excelLogo } from '../../../../assets/images';
import {
    IEarningFormValidationRules,
    IEarningFormValidations,
    IRuleAndEarningModalProps,
    IRuleFormValidationRules,
    IRuleFormValidations,
} from '../../types/interfaces/CampaingTypes';
import { Draggable } from 'react-beautiful-dnd';
import SCUIDraggableList from '../SCUIDraggableList/SCUIDraggableList';
import {
    CampaignFileTypes,
    EarningApplicationScopeType,
    EarningCalculationDiscountType,
    EarningFormInput,
    EarningOptionalSetting,
    EarningProductDiscountScopeType,
    EarningValue,
    RuleAndEarningModalNames,
    RuleCondition,
    RuleExecutionType,
    RuleFormInput,
    RuleSwitchValue,
    RuleType,
} from '../../../../types/enums/campaign-enums';
import { StepEarningAndRuleServiceComponent } from '../../services';
import { SCUIImageGallery, SelectedImageData } from '../SCUIImageGallery/SCUIImageGallery';
import AuthService from '../../../../services/auth/Auth.services';
import Helper from '../../../../utils/helper';
import jwt_decode, { JwtPayload } from 'jwt-decode';
import '../../CampaignManagement.scss';
import { StepRulesTimerComponent } from '../../services/CampaignStepEarningAndRuleService';
import { ConfirmationDialogWithTrigger } from '../SCUIConfirmationDialog/SCUIConfirmationDialogWithTrigger';
import { useSelector } from 'react-redux';
import { CampaignManagementQuery } from '@/services/query';

const mergeCampaignDescriptionTexts = (prev: Record<number, string>, descriptionTexts: any[]) => {
    const newMessages = { ...prev };
    for (const text of descriptionTexts) {
        newMessages[text.languageId] = text.keywordValue;
    }
    return newMessages;
};

const DropzoneRoot = styled('div')({
    display: 'flex',
    border: '2px dashed', // color is set inline where used
    borderRadius: '4px',
    padding: '0 8px',
    height: '52px',
    cursor: 'pointer',
    '&:hover': {
        backgroundColor: '#03a9f41f',
    },
});

export const SCUICampaignEarningsAndRulesStep = ({ ...props }) => {
    const { t } = useTranslation();

    const [isLoading, setIsLoading] = useState(false);
    const [userName, setUserName] = useState('');
    const [coupons, setCoupons] = useState<any[]>([]);

    useEffect(() => {
        const getUserInfo = async () => {
            try {
                const authService = new AuthService();
                const userInfo = await authService.getUserInfo();
                if (!userInfo) {
                    console.error('User info bulunamadı');
                    return;
                }
                if (!Helper.isNil(userInfo)) {
                    const decodedToken: any = jwt_decode<JwtPayload>(userInfo.access_token);
                    setUserName(decodedToken.preferred_username.toLowerCase());
                }
            } catch (error) {
                console.error('Failed to get user info:', error);
            }
        };

        const getCoupons = async () => {
            const campaignId: number = parseInt(id!);
            if (campaignId > 0) {
                setIsLoading(true);
                try {
                    const result = await CampaignManagementQuery.GetCoupons({
                        Id: id,
                        culture: Helper.getLang(),
                    });

                    if (result?.isSuccess && result.data != undefined) {
                        setCoupons(
                            result.data?.map((x: any) => ({
                                id: x.id,
                                name: x.couponName,
                            }))
                        );
                    } else {
                        setCoupons([]);
                    }
                } catch (error) {
                    console.error('Failed to get coupons:', error);
                    setCoupons([]);
                } finally {
                    setIsLoading(false);
                }
            }
        };

        getCoupons();
        getUserInfo();
    }, []); // Run only once on mount

    const regionStore = useSelector((state: any) => state.region);

    const {
        id,
        stepperOnClickReset,
        errors,
        setErrors,
        rules,
        earnings,
        modalInfo,
        PageObjects,
        closeModal,
        saveModal,
        addCondition,
        editCondition,
        getRules,
        addRule,
        handleBulkRuleCreate,
        saveRule,
        deleteRule,
        deleteRuleCondition,
        deleteEarning,
        setRuleForEarning,
        onDragEndRule,
        addEarning,
        editEarning,
        onDragEndEarning,
        downloadFileFilter,
        downloadSlaveExcelFileFilter,
        saveButtonDisabled,
        saveForm,
        saveStep,
    } = StepEarningAndRuleServiceComponent({ ...props });

    const manualEvent = {
        preventDefault: () => {
            console.log('preventDefault is called manually');
        },
    };

    const Item = styled(Paper)(({ theme }) => ({
        ...theme.typography.body2,
        padding: theme.spacing(1),
        color: theme.palette.text.secondary,
    }));

    const SelectRuleWithConfirmation = ({ earningId, ruleId, setRuleId }: any) => {
        const [open, setOpen] = useState(false);
        const [selectedRuleId, setSelectedRuleId] = useState();
        const [message, setMessage] = useState({
            earning: '',
            fromRule: '',
            toRule: '',
        });

        const handleOpen = () => {
            setOpen(true);
        };

        const handleClose = () => {
            setOpen(false);
        };

        const handleConfirm = () => {
            let _errors = Object.keys(errors).filter((x: any) => x != `ruleSelect${earningId}`);

            if (_errors.length == 0) {
                setRuleId(selectedRuleId);
                setRuleForEarning(earningId, selectedRuleId);
            }

            setErrors(_errors);
            handleClose();
        };

        const handleSelectChange = (event: any) => {
            const { value } = event.target;
            setSelectedRuleId(value);
            handleOpen();
        };

        useEffect(() => {
            if (selectedRuleId != undefined) {
                setMessage({
                    earning: earnings.find((x: any) => x.id == earningId)?.name ?? '',
                    fromRule: rules.find((x: any) => x.id == ruleId)?.name ?? "' - '",
                    toRule: rules.find((x: any) => x.id == selectedRuleId)?.name ?? "' - '",
                });
            }
        }, [selectedRuleId]);

        return (
            <div>
                <FormControl style={{ width: '100%' }} error={errors[`ruleSelect${earningId}`] != undefined}>
                    <InputLabel id="ruleSelection">
                        {t('campaignManagement.save.step.rulesAndEarnings.earning.item.ruleSelection')}
                    </InputLabel>
                    <Select
                        required
                        labelId="ruleSelection"
                        id="ruleSelect"
                        name={`ruleSelect${earningId}`}
                        value={ruleId}
                        label={t('campaignManagement.save.step.rulesAndEarnings.earning.item.ruleSelection')}
                        onChange={handleSelectChange}
                    >
                        {rules.length > 0 &&
                            rules.map((item: any) => {
                                return (
                                    <MenuItem value={item.id} key={item.id}>
                                        {item.name}
                                    </MenuItem>
                                );
                            })}
                    </Select>
                    {errors[`ruleSelect${earningId}`] && (
                        <FormHelperText>{errors[`ruleSelect${earningId}`]}</FormHelperText>
                    )}
                </FormControl>
                <Dialog open={open} aria-labelledby="alert-dialog-title" aria-describedby="alert-dialog-description">
                    <DialogTitle id="alert-dialog-title">
                        {t('campaignManagement.save.step.rulesAndEarnings.confirmationDialog.title')}
                    </DialogTitle>
                    <DialogContent dividers>
                        <DialogContentText id="alert-dialog-description" style={{ display: 'flex' }}>
                            <Info style={{ fontSize: 24, color: '#0d6efd', margin: '0 8px' }} />
                            <Typography gutterBottom>
                                <span>
                                    {parse(
                                        DOMPurify.sanitize(
                                            t(
                                                'campaignManagement.save.step.rulesAndEarnings.confirmationDialog.ruleChangeMessage'
                                            )
                                                .replace('{{earning}}', message.earning)
                                                .replace('{{fromRule}}', message.fromRule)
                                                .replace('{{toRule}}', message.toRule)
                                        )
                                    )}
                                </span>
                            </Typography>
                        </DialogContentText>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={handleClose}>
                            {t('campaignManagement.save.step.rulesAndEarnings.confirmationDialog.cancelButton')}
                        </Button>
                        <Button onClick={handleConfirm} autoFocus>
                            {t('campaignManagement.save.step.rulesAndEarnings.confirmationDialog.okButton')}
                        </Button>
                    </DialogActions>
                </Dialog>
            </div>
        );
    };

    const BootstrapDialog = styled(Dialog)(({ theme }) => ({
        '& .MuiDialogContent-root': {
            padding: theme.spacing(2),
        },
        '& .MuiDialogActions-root': {
            padding: theme.spacing(0),
        },
    }));

    const BulkFileUpload = ({
        setUploadResult,
        setIsFileLoading,
    }: {
        setUploadResult: (result: any) => void;
        setIsFileLoading: (loading: boolean) => void;
    }) => {
        const [selectedFile, setSelectedFile] = useState<any>({
            id: 0,
            fileObject: null,
            error: false,
        });

        const onDrop = useCallback(
            async (acceptedFiles: File[]) => {
                const file = acceptedFiles[0] ?? selectedFile.fileObject;

                try {
                    if (file == null) return;

                    let fileTypeId: number = CampaignFileTypes.Option;

                    const formDataObject = new FormData();
                    formDataObject.append('file', file);
                    formDataObject.append('type', String(fileTypeId));
                    formDataObject.append('regionId', regionStore.region);
                    formDataObject.append('creatorUser', userName);

                    setIsFileLoading(true);
                    const response = await CampaignManagementCommand.FileUpload(formDataObject);

                    if (response.isSuccess) {
                        setSelectedFile({
                            id: response.data.id,
                            fileObject: file,
                            error: false,
                        });
                        setIsFileLoading(false);
                        setUploadResult(response);
                        console.log('File(s) uploaded successfully.');
                    } else {
                        setSelectedFile({
                            id: 0,
                            fileObject: file,
                            error: true,
                        });
                        setUploadResult(null);
                        setIsFileLoading(false);
                        console.error('File(s) upload failed.');
                    }
                } catch (error) {
                    setSelectedFile({
                        id: 0,
                        fileObject: file,
                        error: true,
                    });
                    setUploadResult(null);
                    setIsFileLoading(false);
                    console.error('File upload error:', error);
                }
            },
            [regionStore.region, userName, selectedFile.fileObject]
        );

        const { acceptedFiles, getRootProps, getInputProps, isDragActive } = useDropzone({
            onDrop,
            accept: ['.xls', '.xlsx'],
            maxFiles: 1,
        });

        const onRemove = () => {
            setSelectedFile({
                id: 0,
                fileObject: null,
                error: false,
            });
            setUploadResult(null);
        };

        return (
            <div className="container">
                {selectedFile != null && selectedFile.fileObject == null && (
                    <>
                        <DropzoneRoot
                            {...getRootProps()}
                            style={{
                                borderColor: errors.valueFile != undefined ? '#d32f2f' : '#0d6efd',
                            }}
                        >
                            <input id="valueFile" name="valueFile" {...getInputProps()} />
                            <Stack direction="row" alignItems="center" spacing={2}>
                                <CloudUploadOutlined style={{ height: '28px', width: '28px' }} />
                                {isDragActive ? (
                                    <span style={{ width: '100%' }}>
                                        {t('campaignManagement.save.step.rulesAndEarnings.rule.form.file.dragMessage')}
                                    </span>
                                ) : (
                                    <span style={{ width: '100%' }}>
                                        {parse(
                                            DOMPurify.sanitize(
                                                t('campaignManagement.save.step.rulesAndEarnings.rule.form.file.title')
                                            )
                                        )}
                                    </span>
                                )}
                            </Stack>
                        </DropzoneRoot>
                        {errors.valueFile && (
                            <p
                                style={{
                                    color: '#d32f2f',
                                    fontSize: 'small',
                                    margin: '4px 14px 0 14px',
                                }}
                            >
                                {errors.valueFile}
                            </p>
                        )}
                    </>
                )}

                {selectedFile != null && selectedFile.fileObject != null && (
                    <>
                        <Stack
                            direction="row"
                            alignItems="center"
                            spacing={2}
                            style={{
                                border: '2px dashed',
                                borderColor: selectedFile.error ? '#ff1b1c' : '#0d6efd',
                                borderRadius: '4px',
                                padding: '0 8px',
                                backgroundColor: selectedFile.error ? '#ffe9e9' : '#f0f6ff',
                                height: '52px',
                            }}
                        >
                            <img src={excelLogo} style={{ height: '28px', width: '28px' }} />
                            <span style={{ width: '100%' }}>{selectedFile.fileObject.name}</span>
                            {selectedFile.error && (
                                <IconButton
                                    aria-label="retry"
                                    size="large"
                                    onClick={() => onDrop(acceptedFiles)}
                                    sx={{ m: '0!important' }}
                                >
                                    <Refresh fontSize="large" />
                                </IconButton>
                            )}
                            <IconButton
                                aria-label="delete"
                                size="large"
                                onClick={() => onRemove()}
                                sx={{ m: '0!important' }}
                            >
                                <Delete fontSize="large" />
                            </IconButton>
                        </Stack>

                        {selectedFile.error && (
                            <Typography variant="subtitle1" style={{ color: 'red' }}>
                                {selectedFile.fileObject.message ??
                                    t(
                                        'campaignManagement.save.step.rulesAndEarnings.rule.form.file.errorMessage.anErrorOccured'
                                    )}
                            </Typography>
                        )}
                    </>
                )}
            </div>
        );
    };
    const RuleBulkCreateModal: FC<any> = memo(({ name, open }) => {
        if (!open) return <></>;
        const [uploadResult, setUploadResult] = useState<any>();
        const [isFileLoading, setIsFileLoading] = useState(false);
        const dispatch = useDispatch();

        const handleConfirm = async () => {
            if (!uploadResult?.data?.id) return;

            const response = await CampaignManagementCommand.SaveMultipleCampaignFileFilter({
                userFileId: uploadResult.data?.id,
            });

            if (response.isSuccess) {
                closeModal(name);

                dispatch(
                    showSnackbar({
                        message: t('campaignManagement.save.step.rulesAndEarnings.rule.bulkCreate.successMessage'),
                        status: enumSnackbarStatusType.success,
                    })
                );
                console.log('File(s) uploaded successfully.');
            } else {
                console.error('File upload failed.');
                dispatch(
                    showSnackbar({
                        message: t('campaignManagement.save.step.rulesAndEarnings.rule.bulkCreate.errorMessage'),
                        status: enumSnackbarStatusType.danger,
                    })
                );
            }
        };

        return (
            <BootstrapDialog
                onClose={() => closeModal(name)}
                aria-labelledby="customized-dialog-title"
                open={open}
                maxWidth={'sm'}
                sx={{ padding: '16px !important' }}
                fullWidth
            >
                <DialogTitle id="customized-dialog-title" style={{ border: 0 }}>
                    <Typography variant="h6" gutterBottom style={{ fontSize: '2rem' }}>
                        {t('campaignManagement.save.step.rulesAndEarnings.rule.bulkCreate.title')}
                    </Typography>
                </DialogTitle>
                <DialogContent sx={{ padding: '16px !important' }} dividers>
                    <BulkFileUpload setUploadResult={setUploadResult} setIsFileLoading={setIsFileLoading} />
                </DialogContent>
                <DialogActions sx={{ padding: '16px !important' }}>
                    <SCUIButton
                        type="secondary"
                        sizeType="fixed"
                        disabled={isFileLoading}
                        size="s"
                        style={{ marginRight: '16px' }}
                        text={t('general.cancel')}
                        onClick={() => closeModal(name)}
                    />
                    <SCUIButton
                        type="primary"
                        disabled={isFileLoading}
                        sizeType="fixed"
                        size="s"
                        text={t('general.save')}
                        onClick={() => handleConfirm()}
                    />
                </DialogActions>
            </BootstrapDialog>
        );
    });

    const RuleModal: FC<IRuleAndEarningModalProps> = memo(({ name, open, rules }) => {
        // Early return if modal is not open to prevent unnecessary rendering
        if (!open) return null;
        const regionStore = useSelector((state: any) => state.region);

        const RuleForm = memo(() => {
            const { formData } = modalInfo;

            const [formValue, setFormValue] = useState<any>({
                id: { value: formData.id, editable: false },
                ruleConditionId: { value: formData.ruleConditionId, editable: false },
                ruleName: { value: formData.ruleName, editable: false },
                ruleType: {
                    value: formData.ruleType,
                    editable: formData.id == 0 || formData.ruleConditionId == 0 ? true : false,
                },
                condition: { value: formData.condition, editable: true },
                valueSelect: { value: formData.valueSelect, editable: true },
                valueNumber: { value: formData.valueNumber, editable: true },
                valueSwitch: { value: formData.valueSwitch, editable: true },
                valueFile: { value: formData.valueFile, editable: true },
                valueFileName: { value: formData.valueFileName, editable: false },
            });

            const [errors, setErrors] = useState<{ [key: string]: string }>({});
            const [uniqueRuleNameError, setUniqueRuleNameError] = useState<boolean>(false);

            const validations: IRuleFormValidations = {
                ruleName: {
                    required: true,
                    minLength: 2,
                    maxLength: 30,
                },
                ruleType: {
                    required: true,
                },
                condition: {
                    required: true,
                    show: [
                        PageObjects.Rule.ruleTypes.CustomerType,
                        PageObjects.Rule.ruleTypes.Platform,
                        PageObjects.Rule.ruleTypes.DeliveryMethod,
                        PageObjects.Rule.ruleTypes.UsageLimit,
                        PageObjects.Rule.ruleTypes.CustomerInformation,
                        PageObjects.Rule.ruleTypes.ShippingCompany,
                        PageObjects.Rule.ruleTypes.OrderAmount,
                        PageObjects.Rule.ruleTypes.PaymentMethod,
                        PageObjects.Rule.ruleTypes.Quantity,
                        PageObjects.Rule.ruleTypes.CreditCardType,
                        PageObjects.Rule.ruleTypes.Option,
                        PageObjects.Rule.ruleTypes.Store,
                        PageObjects.Rule.ruleTypes.PaymentCollector,
                        PageObjects.Rule.ruleTypes.CouponMethod,
                    ],
                },
                valueSelect: {
                    required: true,
                    show: [
                        PageObjects.Rule.ruleTypes.CustomerType,
                        PageObjects.Rule.ruleTypes.DeliveryMethod,
                        PageObjects.Rule.ruleTypes.PaymentMethod,
                        PageObjects.Rule.ruleTypes.ShippingCompany,
                        PageObjects.Rule.ruleTypes.CreditCardType,
                        PageObjects.Rule.ruleTypes.Platform,
                        PageObjects.Rule.ruleTypes.PaymentCollector,
                        PageObjects.Rule.ruleTypes.CouponMethod,
                    ],
                },
                valueNumber: {
                    required: true,
                    min: 1,
                    max: 999999999999999,
                    show: [PageObjects.Rule.ruleTypes.OrderAmount, PageObjects.Rule.ruleTypes.Quantity],
                },
                valueSwitch: {
                    required: true,
                    show: [PageObjects.Rule.ruleTypes.AtFirstOrder, PageObjects.Rule.ruleTypes.OnlyNewCustomers],
                },
                valueFile: {
                    required: true,
                    show: [
                        PageObjects.Rule.ruleTypes.CustomerInformation,
                        PageObjects.Rule.ruleTypes.Option,
                        PageObjects.Rule.ruleTypes.Store,
                    ],
                },
            };

            const [formCanBeChecked, setFormCanBeChecked] = useState<boolean>(false);

            const [selectedFile, setSelectedFile] = useState<any>({
                id: 0,
                fileObject: null,
                error: false,
            });

            useEffect(() => {
                if (!formCanBeChecked) return;

                const timeoutId = setTimeout(() => {
                    validate();
                }, 100); // Debounce validation

                return () => clearTimeout(timeoutId);
            }, [formCanBeChecked]); // Remove formValue dependency to prevent infinite loop

            useEffect(() => {
                if (modalInfo.editMode) {
                    setSelectedFile({
                        id: formValue.valueFile.value,
                        fileObject: {
                            name: formValue.valueFileName.value,
                            message: formData.message,
                        },
                        error: formData.status == RuleExecutionType.Error,
                    });
                }
            }, []);

            const validateField = (
                fieldName: string,
                value: string,
                rules: IRuleFormValidationRules
            ): string | null => {
                if (rules.required && (!value || value == undefined || value.length == 0)) {
                    return t('campaignManagement.save.step.rulesAndEarnings.rule.form.validationMessage.required');
                }

                if (rules.minLength !== undefined && value.length < rules.minLength) {
                    return t(
                        'campaignManagement.save.step.rulesAndEarnings.rule.form.validationMessage.minLength'
                    ).replace('{{minLength}}', `${rules.minLength}`);
                }

                if (rules.maxLength !== undefined && value.length > rules.maxLength) {
                    return t(
                        'campaignManagement.save.step.rulesAndEarnings.rule.form.validationMessage.maxLength'
                    ).replace('{{maxLength}}', `${rules.maxLength}`);
                }

                if (rules.max !== undefined && (parseFloat(value) || 0) > rules.max) {
                    return t('campaignManagement.save.step.rulesAndEarnings.rule.form.validationMessage.max').replace(
                        '{{max}}',
                        `${rules.max}`
                    );
                }

                if (rules.min !== undefined && (parseFloat(value) || 0) < rules.min) {
                    return t('campaignManagement.save.step.rulesAndEarnings.rule.form.validationMessage.min').replace(
                        '{{min}}',
                        `${rules.min}`
                    );
                }

                return null;
            };

            const validate = () => {
                const newErrors: { [key: string]: string } = {};

                for (const fieldName in validations) {
                    const isShowProp = Object.prototype.hasOwnProperty.call(validations[fieldName], 'show');
                    const foundValidation = isShowProp
                        ? validations[fieldName].show?.some((x) => x == formValue.ruleType.value)
                        : true;

                    let fieldError = null;

                    if (foundValidation) {
                        fieldError = validateField(fieldName, formValue[fieldName].value, validations[fieldName]);
                    }

                    if (fieldError) {
                        newErrors[fieldName] = fieldError;
                    } else {
                        delete newErrors[fieldName];
                    }
                }

                setErrors(newErrors);
                return Object.keys(newErrors).length == 0;
            };

            const handleChange = async (e: any) => {
                let { name, value, checked } = e.target;
                let _formValue = { ...formValue };
                const newErrors: { [key: string]: string } = {};

                let matchFound = false;
                if (name == RuleFormInput.RuleName) {
                    rules.forEach((rule: any) => {
                        if (rule?.name.toLowerCase() === value?.toLowerCase()) {
                            matchFound = true;
                            setUniqueRuleNameError(true);
                            newErrors['uniqueRule'] = t(
                                'campaignManagement.save.step.rulesAndEarnings.rule.form.validationMessage.uniqueRule'
                            );
                        }
                    });

                    if (!matchFound) {
                        setUniqueRuleNameError(false);
                        newErrors['uniqueRule'] = '';
                    }

                    setErrors(newErrors);
                }

                if (name == RuleFormInput.ValueSwitch) {
                    value = checked ? RuleSwitchValue.True : RuleSwitchValue.False;
                }

                if (name == RuleFormInput.RuleType) {
                    switch (value) {
                        case RuleType.AtFirstOrder:
                        case RuleType.NewsCustomerMustJoinedAfterStartDate:
                        case RuleType.OnlyNewCustomers:
                            _formValue = {
                                ..._formValue,
                                condition: {
                                    ..._formValue.condition,
                                    value: RuleCondition.EqualsThan,
                                },
                            };
                            break;
                    }
                }

                if (name.length > 0) {
                    _formValue = {
                        ..._formValue,
                        [name]: { ...formValue[`${name}`], value: value },
                    };

                    setFormValue(_formValue);
                }
            };

            const handleSubmit = (e: any) => {
                e.preventDefault();

                setFormCanBeChecked(true);

                let isValid = validate();
                if (isValid) {
                    setIsLoading(true);
                    saveModal(modalInfo.name, formValue, setIsLoading);
                }
            };

            const visible = useMemo(
                () => (inputName: string) => {
                    return PageObjects.Rule.inputPermissions
                        .find((x: any) => x.key == inputName)
                        ?.show.some((x: any) => x == formValue.ruleType.value);
                },
                [formValue.ruleType.value] // Only depend on ruleType, not ruleName
            );

            const editable = useMemo(
                () => (inputName: string) => {
                    let result: boolean = false;
                    if (!modalInfo.editMode) {
                        //düzenleme modu pasif ise
                        result = true;
                    } else if (modalInfo.editMode && formValue[inputName].editable) {
                        result = true;
                    }
                    return result;
                },
                [formValue.ruleType.value]
            );

            const FileUpload = () => {
                const onDrop = useCallback(
                    async (acceptedFiles: File[]) => {
                        const file = acceptedFiles[0] ?? selectedFile.fileObject;

                        try {
                            if (file == null) return;

                            let fileTypeId: number = CampaignFileTypes.Customer;
                            switch (formValue.ruleType.value) {
                                case RuleType.CustomerInformation:
                                    fileTypeId = CampaignFileTypes.Customer;
                                    break;
                                case RuleType.Option:
                                    fileTypeId = CampaignFileTypes.Option;
                                    break;
                                case RuleType.Store:
                                    fileTypeId = CampaignFileTypes.Store;
                                    break;
                            }

                            const formDataObject = new FormData();
                            formDataObject.append('file', file);
                            formDataObject.append('type', String(fileTypeId));
                            formDataObject.append('regionId', regionStore.region);
                            formDataObject.append('creatorUser', userName);

                            const response = await CampaignManagementCommand.FileUpload(formDataObject);

                            if (response.isSuccess) {
                                setSelectedFile({
                                    id: response.data.id,
                                    fileObject: file,
                                    error: false,
                                });

                                handleChange({
                                    target: { name: 'valueFile', value: response.data.id },
                                });

                                console.log('File(s) uploaded successfully.');
                            } else {
                                setSelectedFile({
                                    id: 0,
                                    fileObject: file,
                                    error: true,
                                });

                                handleChange({
                                    target: { name: 'valueFile', value: 0 },
                                });

                                console.error('File(s) upload failed.');
                            }
                        } catch (error) {
                            setSelectedFile({
                                id: 0,
                                fileObject: file,
                                error: true,
                            });

                            handleChange({
                                target: { name: 'valueFile', value: 0 },
                            });

                            console.error('File upload error:', error);
                        }
                    },
                    [formValue.ruleType.value, regionStore.region, userName, selectedFile.fileObject, handleChange]
                );

                const { acceptedFiles, getRootProps, getInputProps, isDragActive } = useDropzone({
                    onDrop,
                    accept: ['.xls', '.xlsx'],
                    maxFiles: 1,
                });

                const onRemove = () => {
                    setSelectedFile({
                        id: 0,
                        fileObject: null,
                        error: false,
                    });

                    handleChange({
                        target: { name: 'valueFile', value: 0 },
                    });
                };

                return (
                    <div className="container">
                        {selectedFile != null && selectedFile.fileObject == null && (
                            <>
                                <DropzoneRoot
                                    {...getRootProps()}
                                    style={{
                                        borderColor: errors.valueFile != undefined ? '#d32f2f' : '#0d6efd',
                                    }}
                                >
                                    <input id="valueFile" name="valueFile" {...getInputProps()} />
                                    <Stack direction="row" alignItems="center" spacing={2}>
                                        <CloudUploadOutlined style={{ height: '28px', width: '28px' }} />
                                        {isDragActive ? (
                                            <span style={{ width: '100%' }}>
                                                {t(
                                                    'campaignManagement.save.step.rulesAndEarnings.rule.form.file.dragMessage'
                                                )}
                                            </span>
                                        ) : (
                                            <span style={{ width: '100%' }}>
                                                {parse(
                                                    DOMPurify.sanitize(
                                                        t(
                                                            'campaignManagement.save.step.rulesAndEarnings.rule.form.file.title'
                                                        )
                                                    )
                                                )}
                                            </span>
                                        )}
                                    </Stack>
                                </DropzoneRoot>
                                {errors.valueFile && (
                                    <p
                                        style={{
                                            color: '#d32f2f',
                                            fontSize: 'small',
                                            margin: '4px 14px 0 14px',
                                        }}
                                    >
                                        {errors.valueFile}
                                    </p>
                                )}
                            </>
                        )}

                        {selectedFile != null && selectedFile.fileObject != null && (
                            <>
                                <Stack
                                    direction="row"
                                    alignItems="center"
                                    spacing={2}
                                    style={{
                                        border: '2px dashed',
                                        borderColor: selectedFile.error ? '#ff1b1c' : '#0d6efd',
                                        borderRadius: '4px',
                                        padding: '0 8px',
                                        backgroundColor: selectedFile.error ? '#ffe9e9' : '#f0f6ff',
                                        height: '52px',
                                    }}
                                >
                                    <img src={excelLogo} style={{ height: '28px', width: '28px' }} />
                                    <span style={{ width: '100%' }}>{selectedFile.fileObject.name}</span>
                                    {selectedFile.error && (
                                        <IconButton
                                            aria-label="retry"
                                            size="large"
                                            onClick={() => onDrop(acceptedFiles)}
                                            sx={{ m: '0!important' }}
                                        >
                                            <Refresh fontSize="large" />
                                        </IconButton>
                                    )}
                                    <IconButton
                                        aria-label="delete"
                                        size="large"
                                        onClick={() => onRemove()}
                                        sx={{ m: '0!important' }}
                                    >
                                        <Delete fontSize="large" />
                                    </IconButton>
                                </Stack>

                                {selectedFile.error && (
                                    <Typography variant="subtitle1" style={{ color: 'red' }}>
                                        {selectedFile.fileObject.message ??
                                            t(
                                                'campaignManagement.save.step.rulesAndEarnings.rule.form.file.errorMessage.anErrorOccured'
                                            )}
                                    </Typography>
                                )}
                            </>
                        )}
                    </div>
                );
            };

            const FilteredConditionObjects = useMemo(() => {
                const list =
                    PageObjects.Rule.ruleTypeObjects.find((x: any) => x.id == formValue.ruleType.value)?.conditions ??
                    [];

                const sortedList = list
                    .map((x: any) => ({
                        ...x,
                        name: t(x.name),
                    }))
                    .sort((a: any, b: any) => a.name.localeCompare(b.name));

                return sortedList;
            }, [formValue.ruleType.value, t]); // Add t dependency

            const FilteredValueObjects = useMemo(() => {
                if (formValue.ruleType.value === RuleType.CouponMethod) {
                    return coupons;
                }
                const list =
                    PageObjects.Rule.ruleTypeObjects.find((x: any) => x.id == formValue.ruleType.value)?.values ?? [];

                const sortedList = list.sort((a: any, b: any) => a.name.localeCompare(b.name));

                return sortedList;
            }, [formValue.ruleType.value]);

            const renderValueOptions = (options: any, selectedOptions: any, handleChange: any) => {
                let ruleOptions = [];
                if (formValue.ruleType.value === RuleType.CouponMethod) {
                    ruleOptions = coupons;
                } else {
                    ruleOptions = options;
                }
                return ruleOptions.map((option: any) => (
                    <MenuItem key={option.id} value={option.id}>
                        <Checkbox
                            checked={selectedOptions != null ? selectedOptions.indexOf(option.id) > -1 : false}
                            onChange={handleChange}
                        />
                        <ListItemText primary={option.name} />
                    </MenuItem>
                ));
            };

            return (
                <>
                    <DialogContent dividers>
                        <Grid container>
                            <Grid
                                style={{ paddingLeft: 0, margin: '8px' }}
                                size={{
                                    xs: 12,
                                    sm: 12,
                                    md: 12,
                                    lg: 12,
                                }}
                            >
                                <FormControl fullWidth>
                                    <TextField
                                        required
                                        id={PageObjects.Rule.formInput.RuleName}
                                        name={PageObjects.Rule.formInput.RuleName}
                                        label={t(
                                            'campaignManagement.save.step.rulesAndEarnings.rule.form.ruleName.title'
                                        )}
                                        defaultValue={formValue.ruleName.value}
                                        disabled={
                                            modalInfo.name == RuleAndEarningModalNames.RuleCondition ||
                                            (modalInfo.name == RuleAndEarningModalNames.Rule && modalInfo.editMode)
                                        }
                                        onChange={handleChange}
                                        error={errors.ruleName != undefined || uniqueRuleNameError}
                                        helperText={errors.uniqueRule || errors.ruleName}
                                    />
                                </FormControl>
                            </Grid>

                            <Grid
                                style={{ paddingLeft: 0, margin: '8px' }}
                                size={{
                                    xs: 12,
                                    sm: 12,
                                    md: 12,
                                    lg: 12,
                                }}
                            >
                                <FormControl fullWidth>
                                    <TextField
                                        select
                                        required
                                        id={PageObjects.Rule.formInput.RuleType}
                                        name={PageObjects.Rule.formInput.RuleType}
                                        label={t(
                                            'campaignManagement.save.step.rulesAndEarnings.rule.form.ruleType.title'
                                        )}
                                        slotProps={{
                                            select: {
                                                label: '',
                                                multiple: false,
                                                value: formValue.ruleType.value,
                                                disabled: !editable(PageObjects.Rule.formInput.RuleType),
                                            },
                                        }}
                                        onChange={handleChange}
                                        error={errors.ruleType != undefined}
                                        helperText={errors.ruleType}
                                    >
                                        <MenuItem>
                                            {t('campaignManagement.save.step.rulesAndEarnings.rule.select')}
                                        </MenuItem>
                                        {PageObjects.Rule.ruleTypeObjects.length > 0 &&
                                            PageObjects.Rule.ruleTypeObjects
                                                .filter((x: any) =>
                                                    Object.prototype.hasOwnProperty.call(x, 'values')
                                                        ? x.values.length >= 2
                                                        : true
                                                )
                                                .filter((x: any) =>
                                                    x.enabledRegions
                                                        ? x.enabledRegions.includes(Number(regionStore?.region))
                                                        : true
                                                )
                                                .sort((a: any, b: any) => a.name.localeCompare(b.name))
                                                .map((item: any) => {
                                                    return (
                                                        <MenuItem value={item.id} key={item.id}>
                                                            {item.name}
                                                        </MenuItem>
                                                    );
                                                })}
                                    </TextField>
                                </FormControl>
                            </Grid>

                            {visible(PageObjects.Rule.formInput.Condition) && (
                                <Grid
                                    style={{ paddingLeft: 0, margin: '8px' }}
                                    size={{
                                        xs: 12,
                                        sm: 12,
                                        md: 12,
                                        lg: 12,
                                    }}
                                >
                                    <FormControl fullWidth>
                                        <TextField
                                            select
                                            required
                                            id={PageObjects.Rule.formInput.Condition}
                                            name={PageObjects.Rule.formInput.Condition}
                                            label={t(
                                                'campaignManagement.save.step.rulesAndEarnings.rule.form.operator.title'
                                            )}
                                            slotProps={{
                                                select: {
                                                    label: '',
                                                    multiple: false,
                                                    value: formValue.condition.value,
                                                    disabled: !editable(PageObjects.Rule.formInput.Condition),
                                                },
                                            }}
                                            onChange={handleChange}
                                            error={errors.condition != undefined}
                                            helperText={errors.condition}
                                        >
                                            <MenuItem>
                                                {t('campaignManagement.save.step.rulesAndEarnings.rule.select')}
                                            </MenuItem>
                                            {FilteredConditionObjects.length > 0 &&
                                                FilteredConditionObjects.map((item: any) => {
                                                    return (
                                                        <MenuItem
                                                            value={item.id}
                                                            key={item.id}
                                                            disabled={item.disabled}
                                                        >
                                                            {item.name}
                                                        </MenuItem>
                                                    );
                                                })}
                                        </TextField>
                                    </FormControl>
                                </Grid>
                            )}

                            {visible(PageObjects.Rule.formInput.ValueSelect) && (
                                <Grid
                                    style={{ paddingLeft: 0, margin: '8px' }}
                                    size={{
                                        xs: 12,
                                        sm: 12,
                                        md: 12,
                                        lg: 12,
                                    }}
                                >
                                    <FormControl fullWidth error={errors.valueSelect != undefined}>
                                        <InputLabel id="value-label">
                                            {t('campaignManagement.save.step.rulesAndEarnings.rule.form.value.title') +
                                                ' *'}
                                        </InputLabel>
                                        <Select
                                            required
                                            labelId="value-label"
                                            label={t(
                                                'campaignManagement.save.step.rulesAndEarnings.rule.form.value.title'
                                            )}
                                            id={PageObjects.Rule.formInput.ValueSelect}
                                            name={PageObjects.Rule.formInput.ValueSelect}
                                            multiple
                                            value={formValue.valueSelect.value ?? []}
                                            onChange={handleChange}
                                            input={
                                                <OutlinedInput
                                                    label={t(
                                                        'campaignManagement.save.step.rulesAndEarnings.rule.form.value.title'
                                                    )}
                                                />
                                            }
                                            renderValue={(selected) =>
                                                FilteredValueObjects.filter((x: any) => selected.includes(x.id))
                                                    .map((x: any) => x.name)
                                                    .join(', ')
                                            }
                                            disabled={!editable(PageObjects.Rule.formInput.ValueSelect)}
                                        >
                                            {renderValueOptions(
                                                FilteredValueObjects,
                                                formValue.valueSelect.value,
                                                handleChange
                                            )}
                                        </Select>
                                        {errors.valueSelect && <FormHelperText>{errors.valueSelect}</FormHelperText>}
                                    </FormControl>
                                </Grid>
                            )}

                            {visible(PageObjects.Rule.formInput.ValueNumber) && (
                                <Grid
                                    style={{ paddingLeft: 0, margin: '8px' }}
                                    size={{
                                        xs: 12,
                                        sm: 12,
                                        md: 12,
                                        lg: 12,
                                    }}
                                >
                                    <FormControl fullWidth>
                                        <TextField
                                            required
                                            id={PageObjects.Rule.formInput.ValueNumber}
                                            name={PageObjects.Rule.formInput.ValueNumber}
                                            label={t(
                                                'campaignManagement.save.step.rulesAndEarnings.rule.form.value.title'
                                            )}
                                            type="number"
                                            defaultValue={formValue.valueNumber.value ?? null}
                                            disabled={!editable(PageObjects.Rule.formInput.ValueNumber)}
                                            onChange={handleChange}
                                            error={errors.valueNumber != undefined}
                                            helperText={errors.valueNumber}
                                        />
                                    </FormControl>
                                </Grid>
                            )}

                            {visible(PageObjects.Rule.formInput.ValueSwitch) && (
                                <Grid
                                    style={{ paddingLeft: 0, margin: '8px' }}
                                    size={{
                                        xs: 12,
                                        sm: 12,
                                        md: 12,
                                        lg: 12,
                                    }}
                                >
                                    <div
                                        style={{
                                            border: '1px solid #c4c4c4',
                                            padding: '8px 14px',
                                            borderRadius: '4px',
                                        }}
                                    >
                                        <Stack direction="row" spacing={1} alignItems="center">
                                            <Typography>
                                                {t(
                                                    'campaignManagement.save.step.rulesAndEarnings.rule.form.switch.title'
                                                )}
                                            </Typography>
                                            <Switch
                                                id={PageObjects.Rule.formInput.ValueSwitch}
                                                name={PageObjects.Rule.formInput.ValueSwitch}
                                                checked={formValue.valueSwitch.value == RuleSwitchValue.True}
                                                onChange={handleChange}
                                            />
                                        </Stack>
                                    </div>
                                </Grid>
                            )}

                            {visible(PageObjects.Rule.formInput.ValueFile) && (
                                <Grid
                                    style={{ paddingLeft: 0, margin: '8px' }}
                                    size={{
                                        xs: 12,
                                        sm: 12,
                                        md: 12,
                                        lg: 12,
                                    }}
                                >
                                    {/* {modalInfo.editMode && (
                    <Chip
                      label="Dosyayı indir"
                      size="medium"
                      variant="outlined"
                      avatar={<img src={excelLogo} />}
                      style={{ marginRight: "8px", width: "100%" }}
                      // onClick={() => downloadFile(formValue.valueFile.value)}
                      // onDelete={() => downloadFile(formValue.valueFile.value)}
                      deleteIcon={<CloudDownload />}
                    />
                  )}
                  {!modalInfo.editMode && <FileUpload />} */}
                                    <FileUpload />
                                </Grid>
                            )}
                        </Grid>
                    </DialogContent>
                    <DialogActions>
                        <Stack direction="row">
                            <Item style={{ padding: '16px 10px 16px 16px' }}>
                                <SCUIButton
                                    text={t(
                                        'campaignManagement.save.step.rulesAndEarnings.rule.modal.action.cancelButton'
                                    )}
                                    type="secondary"
                                    sizeType="icon"
                                    size={'s'}
                                    onClick={() => closeModal(name)}
                                />
                            </Item>
                            <Item style={{ padding: '16px 16px 16px 10px' }}>
                                <SCUIButton
                                    autoFocusButton={true}
                                    text={t(
                                        'campaignManagement.save.step.rulesAndEarnings.rule.modal.action.saveButton'
                                    )}
                                    type="primary"
                                    sizeType="fixed"
                                    disabled={uniqueRuleNameError || isLoading}
                                    size={'s'}
                                    onClick={() => handleSubmit(manualEvent)}
                                />
                            </Item>
                        </Stack>
                    </DialogActions>
                </>
            );
        });

        const getModalName = () => {
            let name = '';
            if (modalInfo.name == RuleAndEarningModalNames.Rule) {
                name = t('campaignManagement.save.step.rulesAndEarnings.rule.modal.rule.title');
            } else if (modalInfo.name == RuleAndEarningModalNames.RuleCondition) {
                name = !modalInfo.editMode
                    ? t('campaignManagement.save.step.rulesAndEarnings.rule.modal.condition.create.title')
                    : t('campaignManagement.save.step.rulesAndEarnings.rule.modal.condition.edit.title');
            }
            return name;
        };

        const getModalDescription = () => {
            let name = '';
            if (modalInfo.name == RuleAndEarningModalNames.Rule) {
                name = t('campaignManagement.save.step.rulesAndEarnings.rule.modal.rule.description');
            } else if (modalInfo.name == RuleAndEarningModalNames.RuleCondition) {
                name = !modalInfo.editMode
                    ? t('campaignManagement.save.step.rulesAndEarnings.rule.modal.condition.create.description')
                    : t('campaignManagement.save.step.rulesAndEarnings.rule.modal.condition.edit.description');
            }
            return name;
        };

        return (
            <BootstrapDialog
                onClose={() => closeModal(name)}
                aria-labelledby="customized-dialog-title"
                open={open}
                maxWidth={'sm'}
                fullWidth
            >
                <DialogTitle sx={{ m: 2, p: 2 }} id="customized-dialog-title" style={{ border: 0 }}>
                    <Typography variant="h6" gutterBottom style={{ fontSize: '2rem' }}>
                        {getModalName()}
                    </Typography>
                    <Typography variant="body1" gutterBottom style={{ fontSize: '1.5rem' }}>
                        {getModalDescription()}
                    </Typography>
                </DialogTitle>
                <IconButton
                    aria-label="close"
                    onClick={() => closeModal(name)}
                    sx={{
                        position: 'absolute',
                        right: 8,
                        top: 8,
                        color: (theme) => theme.palette.grey[500],
                    }}
                >
                    <Close />
                </IconButton>
                <div className="rule-form">
                    <RuleForm />
                </div>
            </BootstrapDialog>
        );
    });

    const EditableLabel: FC<any> = ({ initialValue, ruleId, rules }) => {
        const [value, setValue] = useState(initialValue);
        const [isEditing, setIsEditing] = useState(false);
        const [errors, setErrors] = useState<any>({});

        const validate = () => {
            const newErrors: { [key: string]: string } = {};

            if (value == undefined || value == null || value.length < 2) {
                newErrors['campaignName'] = t(
                    'campaignManagement.save.step.rulesAndEarnings.rule.form.validationMessage.minLength'
                ).replace('{{minLength}}', `${2}`);
            }

            if (value.length > 2 && value.length > 30) {
                newErrors['campaignName'] = t(
                    'campaignManagement.save.step.rulesAndEarnings.rule.form.validationMessage.maxLength'
                ).replace('{{maxLength}}', `${30}`);
            }

            rules.forEach((rule: any) => {
                if (rule.name.toLowerCase() === value?.toLowerCase() && ruleId !== rule.id) {
                    newErrors['campaignName'] = t(
                        'campaignManagement.save.step.rulesAndEarnings.rule.form.validationMessage.uniqueRule'
                    );
                }
            });

            setErrors(newErrors);
            return Object.keys(newErrors).length == 0;
        };

        useEffect(() => {
            const timeoutId = setTimeout(() => {
                validate();
            }, 200); // Debounce validation

            return () => clearTimeout(timeoutId);
        }, [value]);

        const handleClick = () => {
            setIsEditing(true);
        };

        const handleSave = () => {
            const isValid = validate();
            if (!isValid) return;

            const payload: any = {
                id: ruleId ?? 0,
                campaignRuleId: parseInt(id!),
                name: value,
            };

            saveRule(payload);
            setIsEditing(false);
        };

        const handleChange = (event: any) => {
            const { value } = event.target;
            setValue(value);
        };

        return (
            <div
                style={{
                    width: '100%',
                }}
            >
                {isEditing ? (
                    <div>
                        <FormControl
                            sx={{ width: '100%' }}
                            variant="outlined"
                            error={errors['campaignName'] != undefined}
                        >
                            <OutlinedInput
                                id="campaignName"
                                name="campaignName"
                                type={'text'}
                                autoFocus
                                value={value}
                                onChange={handleChange}
                                size="small"
                                endAdornment={
                                    <InputAdornment position="end">
                                        <IconButton disabled={errors.campaignName} onClick={handleSave} edge="end">
                                            <Done />
                                        </IconButton>
                                    </InputAdornment>
                                }
                            />
                            {errors['campaignName'] != undefined && (
                                <FormHelperText>{errors['campaignName']}</FormHelperText>
                            )}
                        </FormControl>
                    </div>
                ) : (
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                        <Typography
                            style={{
                                fontWeight: '700',
                                marginLeft: '4px',
                                display: 'flex',
                                alignItems: 'center',
                            }}
                        >
                            {value}
                        </Typography>
                        <IconButton onClick={handleClick} style={{ marginLeft: '4px' }}>
                            <Edit />
                        </IconButton>
                    </div>
                )}
            </div>
        );
    };

    const RuleConditionItem = (props: any) => {
        const { filter } = props;

        const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

        const ValueLabel = ({ ...props }) => {
            const { data } = props;
            console.log(data);
            const FileProcessing = (props: any) => {
                const { fileName } = props;

                const processingLabel = t(
                    'campaignManagement.save.step.rulesAndEarnings.rule.item.conditionItem.row.processing'
                );

                const chipTitle = processingLabel;
                const tooltipTitle =
                    fileName != undefined && fileName.length > 0 ? `${fileName} : ${processingLabel}` : processingLabel;
                const color = '#2962ff';
                const logo = <CachedOutlined style={{ color: color }} />;

                return (
                    <Tooltip title={tooltipTitle}>
                        <Chip
                            label={chipTitle}
                            size="small"
                            variant="outlined"
                            avatar={logo}
                            style={{ marginLeft: '4px', marginRight: '8px', borderColor: color, color: color }}
                        />
                    </Tooltip>
                );
            };

            const FileProcessSuccess = (props: any) => {
                const { id, fileName } = props;

                const downloadFileLabel = t(
                    'campaignManagement.save.step.rulesAndEarnings.rule.item.conditionItem.row.downloadFile'
                );

                let chipTitle = '';
                let tooltipTitle = '';

                if (fileName != undefined && fileName.length > 0) {
                    chipTitle = fileName.length <= 15 ? fileName : fileName.slice(0, 15) + '...';
                    tooltipTitle = fileName;
                } else {
                    chipTitle = downloadFileLabel;
                    tooltipTitle = downloadFileLabel;
                }

                const logo = <img src={excelLogo} />;

                return (
                    <Tooltip title={tooltipTitle}>
                        <Chip
                            label={chipTitle}
                            size="small"
                            variant="outlined"
                            avatar={logo}
                            style={{ marginLeft: '4px', marginRight: '8px' }}
                            onClick={() => downloadFileFilter(id)}
                            onDelete={() => downloadFileFilter(id)}
                            deleteIcon={<CloudDownload />}
                        />
                    </Tooltip>
                );
            };

            const FileProcessFailed = (props: any) => {
                const { fileName, message, hasSlaveError, id } = props;
                const [isDownloading, setIsDownloading] = useState(false);
                const failedLabel = t(
                    'campaignManagement.save.step.rulesAndEarnings.rule.item.conditionItem.row.failed'
                );

                const chipTitle = failedLabel;
                const tooltipTitle =
                    fileName != undefined && fileName.length > 0 ? `${fileName} : ${message}` : failedLabel;
                const color = '#d32f2f';
                const logo = <HealingOutlined style={{ color: color }} />;

                const handleSlaveDownload = async () => {
                    if (!id || isDownloading) return;
                    setIsDownloading(true);
                    await downloadSlaveExcelFileFilter(id);
                    setIsDownloading(false);
                };

                return (
                    <Stack direction="column" alignItems="center" spacing={0.5}>
                        <Tooltip
                            title={
                                hasSlaveError
                                    ? t(
                                          'campaignManagement.save.step.rulesAndEarnings.rule.item.conditionItem.row.slaveProductErrorMessage'
                                      )
                                    : tooltipTitle
                            }
                        >
                            <Chip
                                label={chipTitle}
                                size="small"
                                variant="outlined"
                                avatar={logo}
                                style={{ marginLeft: '4px', marginRight: '8px', borderColor: color, color: color }}
                            />
                        </Tooltip>
                        {hasSlaveError && (
                            <Tooltip
                                title={
                                    isDownloading
                                        ? t(
                                              'campaignManagement.save.step.rulesAndEarnings.rule.item.conditionItem.row.processing'
                                          )
                                        : t(
                                              'campaignManagement.save.step.rulesAndEarnings.rule.item.conditionItem.row.slaveDownloadDescription'
                                          )
                                }
                            >
                                <span>
                                    <IconButton
                                        size="small"
                                        color="error"
                                        disabled={isDownloading}
                                        onClick={handleSlaveDownload}
                                        aria-label={t(
                                            'campaignManagement.save.step.rulesAndEarnings.rule.item.conditionItem.row.downloadSlaveExcel'
                                        )}
                                    >
                                        <CloudDownload fontSize="large" />
                                    </IconButton>
                                </span>
                            </Tooltip>
                        )}
                    </Stack>
                );
            };

            let component: any = <></>;

            let values: string[] = data?.campaignFilterValues?.map((x: any) => {
                const ruleType = PageObjects.RuleCondition.ruleTypeObjects.find(
                    (y: any) => y.id == data?.campaignFilterTypeId
                );
                const filteredValues = (ruleType?.values || []).filter((z: any) => z.id == x.value);
                return filteredValues?.map((x: any) => x.name);
            });

            const selectedCoupons = (coupons || []).filter((x: any) =>
                (data?.campaignFilterValues || []).some((y: any) => y.value?.toString() == x.id?.toString())
            );

            switch (data.campaignFilterTypeId) {
                case RuleType.CouponMethod:
                    component = (
                        <Tooltip title={selectedCoupons.map((x: any) => x.name).join(', ')}>
                            <Typography>{selectedCoupons.map((x: any) => x.name).join(', ')}</Typography>
                        </Tooltip>
                    );
                    break;
                case RuleType.CustomerType:
                case RuleType.Platform:
                case RuleType.DeliveryMethod:
                case RuleType.ShippingCompany:
                case RuleType.PaymentMethod:
                case RuleType.PaymentCollector:
                case RuleType.CreditCardType:
                    component = (
                        <Tooltip title={values.join(', ')}>
                            <Typography>{values.join(', ')}</Typography>
                        </Tooltip>
                    );

                    break;
                case RuleType.UsageLimit:
                case RuleType.OrderAmount:
                case RuleType.Quantity:
                    component = <>{data.campaignFilterValues.map((x: any) => x.value)}</>;
                    break;
                case RuleType.AtFirstOrder:
                case RuleType.OnlyNewCustomers:
                case RuleType.NewsCustomerMustJoinedAfterStartDate:
                    component = (
                        <Switch checked={data.campaignFilterValues.some((x: any) => x.value == RuleSwitchValue.True)} />
                    );
                    break;
                case RuleType.CustomerInformation:
                case RuleType.Option:
                case RuleType.Store:
                    switch (data.executionStatus) {
                        case RuleExecutionType.Queue:
                            component = <FileProcessing fileName={data.userFileName} />;
                            break;
                        case RuleExecutionType.Completed:
                            component = <FileProcessSuccess id={data.id} fileName={data.userFileName} />;
                            break;
                        case RuleExecutionType.Error:
                            component = (
                                <FileProcessFailed
                                    fileName={data.userFileName}
                                    message={data.message}
                                    hasSlaveError={data.hasSlaveError}
                                    id={data.id}
                                />
                            );
                            break;
                    }
                    break;
            }

            return component;
        };

        const handleMenuOpen = (event: React.MouseEvent<HTMLButtonElement>) => {
            setAnchorEl(event.currentTarget);
        };

        const handleMenuClose = () => {
            setAnchorEl(null);
        };

        return (
            <Item
                sx={{
                    my: 1,
                    p: 2,
                    textAlign: 'center',
                }}
                style={{ border: '1px solid #dadada' }}
            >
                <Stack
                    spacing={1}
                    direction="row"
                    alignItems="center"
                    style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                    }}
                >
                    <div style={{ width: '35%', textAlign: 'left', paddingLeft: '8px' }}>
                        <Typography noWrap style={{ fontWeight: 'bold' }}>
                            {t(
                                'campaignManagement.save.step.rulesAndEarnings.rule.item.conditionItem.header.condition'
                            )}
                        </Typography>
                        <Typography noWrap>
                            {PageObjects.Rule.ruleTypeObjects.find((r: any) => r.id == filter.campaignFilterTypeId)
                                ?.name ?? ''}
                        </Typography>
                    </div>
                    <div style={{ width: '10%' }}>
                        <Typography noWrap style={{ fontWeight: 'bold' }}>
                            {t('campaignManagement.save.step.rulesAndEarnings.rule.item.conditionItem.header.operator')}
                        </Typography>
                        <Typography noWrap>
                            {PageObjects.Rule.conditionObjects.find((c: any) => c.id == filter.conditionId)?.name ?? ''}
                        </Typography>
                    </div>
                    <div style={{ width: '30%' }}>
                        <Typography noWrap style={{ fontWeight: 'bold' }}>
                            {t('campaignManagement.save.step.rulesAndEarnings.rule.item.conditionItem.header.value')}
                        </Typography>
                        <Typography noWrap>
                            <ValueLabel data={filter} />
                        </Typography>
                    </div>
                    <div style={{ width: '10%' }}>
                        <Typography noWrap style={{ fontWeight: 'bold' }}>
                            {t('campaignManagement.save.step.rulesAndEarnings.rule.item.conditionItem.header.id')}
                        </Typography>
                        <Typography noWrap>{filter.id}</Typography>
                    </div>
                    <div style={{ width: '5%' }}>
                        <IconButton
                            aria-controls={anchorEl != null ? 'basic-menu' : undefined}
                            aria-haspopup="true"
                            aria-expanded={anchorEl != null ? 'true' : undefined}
                            onClick={handleMenuOpen}
                            disabled={filter.executionStatus == RuleExecutionType.Queue}
                        >
                            <MoreHoriz sx={{ fontSize: 24 }} />
                        </IconButton>
                        <Paper sx={{ maxWidth: '100%' }}>
                            <Menu
                                id="basic-menu"
                                anchorEl={anchorEl}
                                open={Boolean(anchorEl)}
                                onClose={handleMenuClose}
                                slotProps={{
                                    list: {
                                        'aria-labelledby': 'basic-button',
                                    },
                                }}
                                anchorOrigin={{
                                    vertical: 'top',
                                    horizontal: 'left',
                                }}
                                transformOrigin={{
                                    vertical: 'top',
                                    horizontal: 'left',
                                }}
                            >
                                <MenuItem onClick={() => editCondition(filter.id, filter.campaignFilterRuleId)}>
                                    <ListItemIcon>
                                        <Edit fontSize="small" />
                                    </ListItemIcon>
                                    <ListItemText>
                                        {t(
                                            'campaignManagement.save.step.rulesAndEarnings.rule.item.conditionItem.action.editButton'
                                        )}
                                    </ListItemText>
                                </MenuItem>
                                <ConfirmationDialogWithTrigger
                                    triggerComponent={
                                        <MenuItem>
                                            <ListItemIcon>
                                                <Close fontSize="small" />
                                            </ListItemIcon>
                                            <ListItemText>
                                                {t(
                                                    'campaignManagement.save.step.rulesAndEarnings.rule.item.conditionItem.action.deleteButton'
                                                )}
                                            </ListItemText>
                                        </MenuItem>
                                    }
                                    title={t(
                                        'campaignManagement.save.step.rulesAndEarnings.rule.item.conditionItem.confirmationDialog.title'
                                    )}
                                    description={t(
                                        'campaignManagement.save.step.rulesAndEarnings.rule.item.conditionItem.confirmationDialog.description'
                                    )}
                                    onConfirm={() => {
                                        deleteRuleCondition(filter.id);
                                    }}
                                />
                            </Menu>
                        </Paper>
                    </div>
                </Stack>
            </Item>
        );
    };

    const DraggableRuleItem: FC<any> = ({ rule, index }) => {
        const { id, name, campaignFilters } = rule;
        return (
            <Draggable draggableId={id.toString()} index={index}>
                {(provided, snapshot) => (
                    <div {...provided.draggableProps} ref={provided.innerRef}>
                        <Card
                            style={{
                                border: '1px solid #dadada',
                                padding: '16px',
                                marginBottom: '16px',
                                background: snapshot.isDragging ? 'rgb(243,247,255)' : undefined,
                            }}
                        >
                            <div style={{ display: 'flex', alignItems: 'center' }}>
                                <Typography variant="subtitle1" style={{ minWidth: '80px' }}>
                                    {t('campaignManagement.save.step.rulesAndEarnings.rule.item.ruleName')} {`(${id})`}
                                </Typography>
                                <EditableLabel initialValue={name} ruleId={id} rules={rules} />
                                <div>
                                    <ConfirmationDialogWithTrigger
                                        triggerComponent={
                                            <IconButton>
                                                <DeleteOutlined sx={{ fontSize: 24 }} />
                                            </IconButton>
                                        }
                                        title={t(
                                            'campaignManagement.save.step.rulesAndEarnings.rule.item.confirmationDialog.title'
                                        )}
                                        description={t(
                                            'campaignManagement.save.step.rulesAndEarnings.rule.item.confirmationDialog.description'
                                        )}
                                        onConfirm={() => {
                                            deleteRule(id);
                                        }}
                                    />
                                </div>
                            </div>

                            <Divider
                                style={{
                                    width: '100%',
                                    margin: '16px 0',
                                    color: '#dadada',
                                }}
                            />

                            {campaignFilters.length > 0 &&
                                campaignFilters.map((filter: any, index: number) => {
                                    return <RuleConditionItem filter={filter} key={index} />;
                                })}

                            {campaignFilters.length == 0 && (
                                <Item
                                    sx={{
                                        my: 1,
                                        p: 2,
                                    }}
                                    style={{ border: '1px solid #dadada', textAlign: 'center', color: '#d32f2f' }}
                                >
                                    <Typography noWrap>
                                        {t('campaignManagement.save.step.rulesAndEarnings.rule.item.noRecordMessage')}
                                    </Typography>
                                </Item>
                            )}

                            <div
                                style={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                }}
                            >
                                <Typography {...provided.dragHandleProps} noWrap style={{ fontWeight: 'bold' }}>
                                    <DragIndicator sx={{ width: '24px', height: '24px' }} />
                                </Typography>

                                <SCUIButton
                                    text={t(
                                        'campaignManagement.save.step.rulesAndEarnings.rule.item.action.createConditionButton'
                                    )}
                                    type="ghost"
                                    buttonType="button"
                                    sizeType="default"
                                    size={'s'}
                                    showStartIcon={true}
                                    startIcon={<AddCircle />}
                                    onClick={() => addCondition(id)}
                                    style={{ float: 'right' }}
                                />
                            </div>
                        </Card>
                    </div>
                )}
            </Draggable>
        );
    };

    interface TabPanelProps {
        children?: React.ReactNode;
        index: number;
        value: number;
    }

    const TabPanel = (props: TabPanelProps) => {
        const { children, value, index, ...other } = props;
        return (
            <div
                role="tabpanel"
                hidden={value !== index}
                id={`earning-tabpanel-${index}`}
                aria-labelledby={`earning-tab-${index}`}
                style={{ display: value !== index ? 'none' : 'block' }}
                {...other}
            >
                <Box>{children}</Box>
            </div>
        );
    };

    const EarningModal: FC<IRuleAndEarningModalProps> = memo(({ name, open }) => {
        // Early return if modal is not open to prevent unnecessary rendering
        if (!open) return null;

        const [activeTab, setActiveTab] = useState(0);

        // Get languages from localStorage
        const countryId: any = JSON.parse(localStorage.getItem('selectedCountry') || '{}')?.countryId?.toString();
        const [languages] = useState(() => {
            try {
                return JSON.parse(import.meta.env.VITE_LANGUAGE_LIST || '[]').filter((x: any) => {
                    return x.countries?.includes(countryId?.toString());
                });
            } catch {
                return [];
            }
        });

        // Badge and campaign message state for second tab
        const [badgeData, setBadgeData] = useState<any>([]);

        const [campaignMessages, setCampaignMessages] = useState<any>(() => {
            const initialMessages: any = {};
            languages.forEach((lang: any) => {
                initialMessages[lang.id] = '';
            });
            return initialMessages;
        });

        useEffect(() => {
            if (modalInfo?.formData?.badges) {
                setBadgeData(() => modalInfo?.formData?.badges ?? []);
            }
        }, [modalInfo?.formData?.id]);

        useEffect(() => {
            if (modalInfo?.formData?.descriptionTexts) {
                setCampaignMessages((prev: any) =>
                    mergeCampaignDescriptionTexts(prev, modalInfo.formData.descriptionTexts)
                );
            }
        }, [modalInfo?.formData?.id]);
        // Image gallery modal state for badge selection
        const [isImageGalleryOpen, setIsImageGalleryOpen] = useState(false);
        const [selectedBadgeTarget, setSelectedBadgeTarget] = useState<{
            languageCode: string;
            platform: string;
        } | null>(null);

        // Ref to preserve EarningForm state across tab changes
        const earningFormStateRef = useRef<any>(null);

        // Ref to track the latest badgeData for logging (since state updates are async)
        const latestBadgeDataRef = useRef<any>(badgeData);

        // Keep the ref updated with the latest badgeData
        useEffect(() => {
            latestBadgeDataRef.current = badgeData;
        }, [badgeData]);

        const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
            setActiveTab(newValue);
        };

        const handleBadgeChange = (languageCode: string, platform: string, value: string) => {
            setBadgeData((prev: any) => {
                const newData = [...prev];
                const existingBadge = newData.find((badge: any) => badge.culture === languageCode);
                if (existingBadge) {
                    existingBadge[platform] = value;
                } else {
                    newData.push({
                        culture: languageCode,
                        web: platform === 'web' ? value : '',
                        mobileWeb: platform === 'mobileWeb' ? value : '',
                        mobileApp: platform === 'mobileApp' ? value : '',
                    });
                }
                return newData;
            });
        };

        const handleCampaignMessageChange = (languageId: string, value: string) => {
            setCampaignMessages((prev: any) => ({
                ...prev,
                [languageId]: value,
            }));
        };

        // Separate component for campaign message input to prevent re-rendering on every keystroke
        const CampaignMessageInput = ({
            languageId,
            languageName,
            initialValue,
            onBlurUpdate,
        }: {
            languageId: string;
            languageName: string;
            initialValue: string;
            onBlurUpdate: (languageId: string, value: string) => void;
        }) => {
            // Manage input value locally to prevent parent re-renders on every keystroke
            const [localValue, setLocalValue] = useState(initialValue);

            // Update local value if initialValue changes (e.g., when modal reopens)
            useEffect(() => {
                setLocalValue(initialValue);
            }, [initialValue]);

            return (
                <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>
                        {languageName}
                    </Typography>
                    <FormControl fullWidth>
                        <TextField
                            id={`campaignMessage-${languageId}`}
                            name={`campaignMessage-${languageId}`}
                            label={t(
                                'campaignManagement.save.step.rulesAndEarnings.earning.modal.websiteAppearanceTab.campaignMessageTitle'
                            )}
                            placeholder={t(
                                'campaignManagement.save.step.rulesAndEarnings.earning.modal.websiteAppearanceTab.campaignMessagePlaceholder'
                            )}
                            value={localValue}
                            onChange={(e) => setLocalValue(e.target.value)}
                            onBlur={() => onBlurUpdate(languageId, localValue)}
                            size="small"
                        />
                    </FormControl>
                </Box>
            );
        };

        const openBadgeGallery = (languageCode: string, platform: string) => {
            setSelectedBadgeTarget({ languageCode, platform });
            setIsImageGalleryOpen(true);
        };

        const closeBadgeGallery = () => {
            // Note: badgeData here might be stale due to async state updates
            // Use latestBadgeDataRef for the most recent value
            setIsImageGalleryOpen(false);
            setSelectedBadgeTarget(null);
        };

        const handleBadgeSelect = (imageUrl: string, imageData?: SelectedImageData) => {
            // Use culture and assetTarget from imageData if available (new API), otherwise fall back to selectedBadgeTarget
            const culture = imageData?.culture || selectedBadgeTarget?.languageCode;
            const assetTarget = imageData?.assetTarget || selectedBadgeTarget?.platform;

            if (culture && assetTarget) {
                handleBadgeChange(culture, assetTarget, imageUrl);
            } else {
                console.warn('EarningModal - Missing culture or assetTarget, badge not updated');
            }
            closeBadgeGallery();
        };

        const deleteBadge = (languageCode: string, platform: string) => {
            handleBadgeChange(languageCode, platform, '');
        };

        const BootstrapDialog = styled(Dialog)(({ theme }) => ({
            '& .MuiDialogContent-root': {
                padding: theme.spacing(2),
            },
            '& .MuiDialogActions-root': {
                padding: theme.spacing(0),
            },
        }));

        const EarningForm = ({ formStateRef }: { formStateRef: React.MutableRefObject<any> }) => {
            const { editMode, formData } = modalInfo;

            // Initialize from ref if available (preserves state across tab changes), otherwise use default
            const getInitialFormValue = () => {
                if (formStateRef.current?.formValue) {
                    return formStateRef.current.formValue;
                }
                return {
                    id: { value: formData.id, editable: false },
                    campaignFilterRuleId: {
                        value: formData.campaignFilterRuleId,
                        editable: true,
                    },
                    name: { value: formData.name, editable: true },
                    calculationDiscountType: {
                        value: formData.calculationDiscountType,
                        editable: true,
                    },
                    value: { value: formData.value, editable: true },
                    optionalSetting: { value: formData.optionalSetting, editable: true },
                    priceSortingType: { value: formData.priceSortingType, editable: true },
                    maxDiscountAmount: {
                        value: formData.maxDiscountAmount,
                        editable: true,
                    },
                    maxDiscountedProductCount: {
                        value: formData.maxDiscountedProductCount,
                        editable: true,
                    },
                    numberOfFreeItems: {
                        value: formData.numberOfFreeItems,
                        editable: true,
                    },
                    numberOfItemPurchased: {
                        value: formData.numberOfItemPurchased,
                        editable: true,
                    },
                    numberOfItemPurchasedNthProduct: {
                        value: formData.numberOfItemPurchased,
                        editable: true,
                    },
                    onlyNonDiscountedProducts: {
                        value: formData.onlyNonDiscountedProducts == EarningValue.True,
                        editable: true,
                    },
                    applicationScopeType: {
                        value: formData.applicationScopeType,
                        editable: true,
                    },
                    productDiscountScopeType: {
                        value: formData.productDiscountScopeType,
                        editable: true,
                    },
                    distributionType: { value: formData.distributionType, editable: true },
                    useDiscountedPrice: {
                        value: formData.useDiscountedPrice == EarningValue.True,
                        editable: true,
                    },
                    includeShippingPriceToGrandTotal: {
                        value: formData.includeShippingPriceToGrandTotal == EarningValue.True,
                        editable: true,
                    },
                    showDiscountedPrice: {
                        value: formData.showDiscountedPrice == EarningValue.True,
                        editable: true,
                    },
                };
            };

            const [formValue, setFormValueInternal] = useState<any>(getInitialFormValue);

            // Wrapper to update both local state and ref
            const setFormValue = (newValue: any) => {
                const value = typeof newValue === 'function' ? newValue(formValue) : newValue;
                setFormValueInternal(value);
                if (!formStateRef.current) {
                    formStateRef.current = {};
                }
                formStateRef.current.formValue = value;
            };

            const [errors, setErrors] = useState<{ [key: string]: string }>({});
            const [uniqueEarningNameError, setUniqueEarningNameError] = useState<boolean>(false);

            const [formCanBeChecked, setFormCanBeChecked] = useState<boolean>(false);
            const [formChanged, setFormChanged] = useState<boolean | undefined>(false);

            const [permissions, setPermissions] = useState([
                {
                    key: EarningFormInput.CampaignFilterRuleId,
                    visible: true,
                },
                {
                    key: EarningFormInput.ApplicationScopeType,
                    visible: true,
                },
                {
                    key: EarningFormInput.CalculationDiscountType,
                    visible: false,
                },
                {
                    key: EarningFormInput.Value,
                    visible: false,
                },

                {
                    key: EarningFormInput.OptionalSetting,
                    visible: false,
                },
                {
                    key: EarningFormInput.NumberOfItemPurchasedNthProduct,
                    visible: false,
                },
                {
                    key: EarningFormInput.NumberOfItemPurchased,
                    visible: false,
                },
                {
                    key: EarningFormInput.NumberOfFreeItems,
                    visible: false,
                },
                {
                    key: EarningFormInput.MaxDiscountAmount,
                    visible: false,
                },
                {
                    key: EarningFormInput.IncludeShippingPriceToGrandTotal,
                    visible: false,
                },
                {
                    key: EarningFormInput.UseDiscountedPrice,
                    visible: false,
                },
                {
                    key: EarningFormInput.OnlyNonDiscountedProducts,
                    visible: false,
                },
                {
                    key: EarningFormInput.PriceSortingType,
                    visible: false,
                },
                {
                    key: EarningFormInput.MaxDiscountedProductCount,
                    visible: false,
                },
                {
                    key: EarningFormInput.DistributionType,
                    visible: false,
                },
                {
                    key: EarningFormInput.ShowDiscountedPrice,
                    visible: false,
                },
            ]);

            const visible = useCallback(
                (inputName: string) => {
                    return permissions.find((x: any) => x.key == inputName)?.visible;
                },
                [permissions]
            );

            const setVisible = (inputName: string) => {
                let _permissions = [...permissions];
                let _index = _permissions.findIndex((x: any) => x.key == inputName);
                _permissions[_index].visible = true;

                setPermissions(_permissions);
            };

            const setInvisible = (inputName: string) => {
                //permissions
                let _permissions = [...permissions];
                let _index = _permissions.findIndex((x: any) => x.key == inputName);
                _permissions[_index].visible = false;

                setPermissions(_permissions);
            };

            const setInvisibleMultiParams = (inputNames: string[]) => {
                let _permissions = [...permissions];

                inputNames.map((name: string) => {
                    //permissions
                    let _index = _permissions.findIndex((x: any) => x.key == name);
                    _permissions[_index].visible = false;
                });

                setPermissions(_permissions);
            };

            const setParamsForOptionalSettings = (inputValues: number[]) => {
                let _formValue = { ...formValue };

                _formValue = {
                    ...formValue,
                    optionalSetting: {
                        ...formValue.optionalSetting,
                        value: inputValues == null || inputValues.length == 0 ? [] : inputValues,
                    },
                };

                let inputNames: string[] = [];
                inputValues.map((x: any) => {
                    const optionalSettingName: string = EarningOptionalSetting[x];
                    const optionalSettingInputName: EarningFormInput =
                        EarningFormInput[optionalSettingName as keyof typeof EarningFormInput];
                    inputNames.push(optionalSettingInputName);
                });

                const optionalSettingObjects = Object.entries(EarningOptionalSetting)
                    .filter((e) => !isNaN(e[0] as any))
                    .map((e) => ({ id: e[0], name: e[1] }));

                optionalSettingObjects.map((item: any) => {
                    const optionName: EarningFormInput = EarningFormInput[item.name as keyof typeof EarningFormInput];
                    const isExist = inputNames.some((name) => name == optionName);

                    if (!isExist) {
                        const defaultValue: any = PageObjects.Earning.initialData[optionName];

                        _formValue = {
                            ..._formValue,
                            [optionName]: { ...formValue[`${optionName}`], value: defaultValue },
                        };
                    }
                });

                setFormValue(_formValue);
            };

            const isVisible = (inputName: string) => {
                let _permissions = [...permissions];
                let _item = _permissions.find((x: any) => x.key == inputName);

                return _item?.visible;
            };

            const checkVisible = () => {
                const discountApplyToProduct = `${EarningApplicationScopeType.Product}_${EarningProductDiscountScopeType.ApplyToProduct}`;
                const discountApplyToNthProducts = `${EarningApplicationScopeType.Product}_${EarningProductDiscountScopeType.ApplyToNthProducts}`;
                const discountBuyNPayY = `${EarningApplicationScopeType.Product}_${EarningProductDiscountScopeType.BuyNPayY}`;

                if (formValue.name.value.length >= 2) {
                    setVisible(EarningFormInput.CampaignFilterRuleId);
                    setVisible(EarningFormInput.ApplicationScopeType);
                }

                if (!isVisible(EarningFormInput.CampaignFilterRuleId)) {
                    setInvisibleMultiParams([
                        EarningFormInput.ApplicationScopeType,
                        EarningFormInput.CalculationDiscountType,
                        EarningFormInput.Value,
                        EarningFormInput.NumberOfItemPurchasedNthProduct,
                        EarningFormInput.NumberOfItemPurchased,
                        EarningFormInput.NumberOfFreeItems,
                    ]);
                }

                switch (formValue.applicationScopeType.value) {
                    case EarningApplicationScopeType.Shipping:
                    case EarningApplicationScopeType.Order:
                    case discountApplyToProduct:
                        setVisible(EarningFormInput.CalculationDiscountType);
                        setVisible(EarningFormInput.Value);

                        setInvisibleMultiParams([
                            EarningFormInput.NumberOfItemPurchasedNthProduct,
                            EarningFormInput.NumberOfItemPurchased,
                            EarningFormInput.NumberOfFreeItems,
                        ]);
                        break;
                    case discountApplyToNthProducts:
                        setVisible(EarningFormInput.NumberOfItemPurchasedNthProduct);
                        setVisible(EarningFormInput.CalculationDiscountType);
                        setVisible(EarningFormInput.Value);

                        setInvisibleMultiParams([
                            EarningFormInput.NumberOfItemPurchased,
                            EarningFormInput.NumberOfFreeItems,
                        ]);
                        break;
                    case discountBuyNPayY:
                        setVisible(EarningFormInput.NumberOfItemPurchased);
                        setVisible(EarningFormInput.NumberOfFreeItems);
                        setVisible(EarningFormInput.CalculationDiscountType);
                        setVisible(EarningFormInput.Value);

                        setInvisibleMultiParams([EarningFormInput.NumberOfItemPurchasedNthProduct]);
                        break;
                }
            };

            const checkVisibleForOptional = () => {
                let visibleItemList = FilteredOptionalSettings.filter((x: any) =>
                    formValue.optionalSetting.value.includes(x.id)
                ).map((x: any) => x.key);

                let invisibleItemList = FilteredOptionalSettings.filter(
                    (x: any) => !formValue.optionalSetting.value.includes(x.id)
                ).map((x: any) => x.key);

                let _permissions = [...permissions];
                _permissions.map((p) => {
                    if (visibleItemList.includes(p.key)) {
                        p.visible = true;
                    } else if (invisibleItemList.includes(p.key)) {
                        p.visible = false;
                    }
                });

                setPermissions(_permissions);
            };

            const [validations, setValidations] = useState<IEarningFormValidations>({
                name: {
                    required: true,
                    minLength: 2,
                    maxLength: 30,
                },
                campaignFilterRuleId: {
                    required: true,
                },
                applicationScopeType: {
                    required: true,
                },
                calculationDiscountType: {
                    visible: isVisible(EarningFormInput.CalculationDiscountType),
                    required: true,
                },
                value: {
                    visible: isVisible(EarningFormInput.Value),
                    required: true,
                    min: 1,
                    max:
                        EarningCalculationDiscountType.Percentage == formValue.calculationDiscountType.value
                            ? 100
                            : 999999999999999,
                },
                numberOfItemPurchasedNthProduct: {
                    visible: isVisible(EarningFormInput.NumberOfItemPurchasedNthProduct),
                    required: true,
                    min: 1,
                },
                numberOfItemPurchased: {
                    visible: isVisible(EarningFormInput.NumberOfItemPurchased),
                    required: true,
                    min: 2,
                },
                numberOfFreeItems: {
                    visible: isVisible(EarningFormInput.NumberOfFreeItems),
                    required: true,
                    min: 1,
                },
                maxDiscountAmount: {
                    visible: isVisible(EarningFormInput.MaxDiscountAmount),
                    required: true,
                    min: 1,
                    max: 50,
                },
                maxDiscountedProductCount: {
                    visible: isVisible(EarningFormInput.MaxDiscountedProductCount),
                    required: true,
                    min: 1,
                },
            });

            useEffect(() => {
                checkVisible();
                checkVisibleForOptional();
            }, [formValue.applicationScopeType.value, formValue.optionalSetting.value]);

            useEffect(() => {
                if (!formCanBeChecked) return;

                const timeoutId = setTimeout(() => {
                    validate();
                }, 100); // Debounce validation

                return () => clearTimeout(timeoutId);
            }, [formCanBeChecked]);

            useEffect(() => {
                if (!formChanged) return;

                setValidations({
                    ...validations,
                    calculationDiscountType: {
                        ...validations.calculationDiscountType,
                        visible: isVisible(EarningFormInput.CalculationDiscountType),
                    },
                    value: {
                        ...validations.value,
                        visible: isVisible(EarningFormInput.Value),
                        max:
                            EarningCalculationDiscountType.Percentage == formValue.calculationDiscountType.value
                                ? 100
                                : 999999999999999,
                    },
                    numberOfItemPurchasedNthProduct: {
                        ...validations.numberOfItemPurchasedNthProduct,
                        visible: isVisible(EarningFormInput.NumberOfItemPurchasedNthProduct),
                    },
                    numberOfItemPurchased: {
                        ...validations.numberOfItemPurchased,
                        visible: isVisible(EarningFormInput.NumberOfItemPurchased),
                    },
                    numberOfFreeItems: {
                        ...validations.numberOfFreeItems,
                        visible: isVisible(EarningFormInput.NumberOfFreeItems),
                    },
                    maxDiscountAmount: {
                        ...validations.maxDiscountAmount,
                        visible: isVisible(EarningFormInput.MaxDiscountAmount),
                    },
                    maxDiscountedProductCount: {
                        ...validations.maxDiscountedProductCount,
                        visible: isVisible(EarningFormInput.MaxDiscountedProductCount),
                    },
                });

                setFormChanged(undefined);
            }, [permissions]);

            useEffect(() => {
                if (
                    formValue?.applicationScopeType?.value !== null &&
                    formValue?.applicationScopeType?.value !== undefined
                ) {
                    setVisible(EarningFormInput.OptionalSetting);
                    return;
                }

                setInvisible(EarningFormInput.OptionalSetting);
            }, [formValue]);

            const validateField = (
                fieldName: string,
                value: string,
                rules: IEarningFormValidationRules
            ): string | null => {
                if (rules.visible != undefined && !rules.visible) return null;

                if (rules.required && (value == undefined || value == null || value.length == 0)) {
                    return t('campaignManagement.save.step.rulesAndEarnings.earning.form.validationMessage.required');
                }

                if (rules.minLength !== undefined && value.length < rules.minLength) {
                    return t(
                        'campaignManagement.save.step.rulesAndEarnings.earning.form.validationMessage.minLength'
                    ).replace('{{minLength}}', `${rules.minLength}`);
                }

                if (rules.maxLength !== undefined && value.length > rules.maxLength) {
                    return t(
                        'campaignManagement.save.step.rulesAndEarnings.earning.form.validationMessage.maxLength'
                    ).replace('{{maxLength}}', `${rules.maxLength}`);
                }

                if (rules.min !== undefined && (parseFloat(value) || 0) < rules.min) {
                    return t(
                        'campaignManagement.save.step.rulesAndEarnings.earning.form.validationMessage.min'
                    ).replace('{{min}}', `${rules.min}`);
                }

                if (rules.max !== undefined && (parseFloat(value) || 0) > rules.max) {
                    return t(
                        'campaignManagement.save.step.rulesAndEarnings.earning.form.validationMessage.max'
                    ).replace('{{max}}', `${rules.max}`);
                }

                return null;
            };

            const validate = () => {
                const newErrors: { [key: string]: string } = {};

                for (const fieldName in validations) {
                    const isShowProp = Object.prototype.hasOwnProperty.call(validations[fieldName], 'show');
                    const foundValidation = isShowProp
                        ? validations[fieldName].show?.some((x) => x == formValue.campaignFilterRuleId.value)
                        : true;

                    let fieldError = null;

                    if (foundValidation) {
                        fieldError = validateField(fieldName, formValue[fieldName].value, validations[fieldName]);
                    }

                    if (fieldError) {
                        newErrors[fieldName] = fieldError;
                    } else {
                        delete newErrors[fieldName];
                    }
                }

                setErrors(newErrors);
                return Object.keys(newErrors).length == 0;
            };

            const handleChange = (e: any) => {
                let { name, value, checked } = e.target;

                let _formValue = { ...formValue };

                const newErrors: { [key: string]: string } = {};

                let matchFound = false;
                if (name == EarningFormInput.Name) {
                    earnings.forEach((earning: any) => {
                        if (earning?.name.toLowerCase() === value?.toLowerCase()) {
                            matchFound = true;
                            setUniqueEarningNameError(true);
                            newErrors['uniqueEarning'] = t(
                                'campaignManagement.save.step.rulesAndEarnings.earning.form.validationMessage.uniqueEarning'
                            );
                        }
                    });

                    if (!matchFound) {
                        setUniqueEarningNameError(false);
                        newErrors['uniqueEarning'] = '';
                    }

                    setErrors(newErrors);
                }

                switch (name) {
                    case EarningFormInput.OnlyNonDiscountedProducts:
                    case EarningFormInput.IncludeShippingPriceToGrandTotal:
                    case EarningFormInput.ShowDiscountedPrice:
                        value = checked ? EarningValue.True : EarningValue.False;
                        break;
                }

                _formValue = {
                    ...formValue,
                    [name]: { ...formValue[`${name}`], value: value },
                };

                if (
                    name === PageObjects.Earning.formInput.CalculationDiscountType ||
                    name === PageObjects.Earning.formInput.ApplicationScopeType
                ) {
                    _formValue = {
                        ..._formValue,
                        [PageObjects.Earning.formInput.Value]: {
                            ...formValue[`${PageObjects.Earning.formInput.Value}`],
                            value: 0,
                        },
                    };

                    if (name === PageObjects.Earning.formInput.ApplicationScopeType) {
                        setVisible(PageObjects.Earning.formInput.ApplicationScopeType);
                    }
                }

                setFormChanged(true);
                setFormValue(_formValue);
            };

            const handleChangeOptionalSetting = (e: any) => {
                const { value } = e.target;
                setFormChanged(true);

                setParamsForOptionalSettings(value);

                let inputNames: string[] = [];
                value.map((x: any) => {
                    const optionalSettingName: string = EarningOptionalSetting[x];
                    const optionalSettingInputName: EarningFormInput =
                        EarningFormInput[optionalSettingName as keyof typeof EarningFormInput];
                    inputNames.push(optionalSettingInputName);
                });

                setInvisibleMultiParams(inputNames);
            };

            const handleDeleteOptionalSetting = (inputValue: number) => {
                const filteredValueList = formValue.optionalSetting.value.filter((x: any) => x != inputValue);
                setParamsForOptionalSettings(filteredValueList);

                const optionalSettingName: string = EarningOptionalSetting[inputValue];
                const inputName: EarningFormInput =
                    EarningFormInput[optionalSettingName as keyof typeof EarningFormInput];
                setInvisible(inputName);
            };

            const dataMapping = () => {
                let _formValue = { ...formValue };

                _formValue.campaignFilterRuleId.value = isVisible(EarningFormInput.CampaignFilterRuleId)
                    ? formValue.campaignFilterRuleId.value
                    : PageObjects.Earning.initialData.campaignFilterRuleId;
                _formValue.calculationDiscountType.value = isVisible(EarningFormInput.CalculationDiscountType)
                    ? formValue.calculationDiscountType.value
                    : PageObjects.Earning.initialData.calculationDiscountType;
                _formValue.value.value = isVisible(EarningFormInput.Value)
                    ? formValue.value.value
                    : PageObjects.Earning.initialData.value;
                _formValue.applicationScopeType.value = isVisible(EarningFormInput.ApplicationScopeType)
                    ? formValue.applicationScopeType.value
                    : PageObjects.Earning.initialData.applicationScopeType;
                _formValue.maxDiscountedProductCount.value = isVisible(EarningFormInput.MaxDiscountedProductCount)
                    ? formValue.maxDiscountedProductCount.value
                    : PageObjects.Earning.initialData.maxDiscountedProductCount;
                _formValue.numberOfFreeItems.value = isVisible(EarningFormInput.NumberOfFreeItems)
                    ? formValue.numberOfFreeItems.value
                    : _formValue.applicationScopeType.value ==
                        `${EarningApplicationScopeType.Product}_${EarningProductDiscountScopeType.ApplyToNthProducts}`
                      ? 1 //sabit değer
                      : PageObjects.Earning.initialData.numberOfFreeItems;

                if (isVisible(EarningFormInput.NumberOfItemPurchased)) {
                    _formValue.numberOfItemPurchased.value = formValue.numberOfItemPurchased.value;
                } else if (isVisible(EarningFormInput.NumberOfItemPurchasedNthProduct)) {
                    _formValue.numberOfItemPurchased.value = formValue.numberOfItemPurchasedNthProduct.value;
                } else {
                    _formValue.numberOfItemPurchased.value = PageObjects.Earning.initialData.numberOfItemPurchased;
                }

                return _formValue;
            };

            const handleSubmit = async (e: any) => {
                e.preventDefault();

                setFormCanBeChecked(true);

                let isValid = validate();
                if (isValid) {
                    setIsLoading(true);
                    const _formValue = dataMapping();

                    _formValue.badges = badgeData;
                    _formValue.descriptionTexts = Object.entries(campaignMessages).map(
                        ([languageId, keywordValue]) => ({
                            languageId: Number(languageId),
                            keywordValue: typeof keywordValue === 'string' ? keywordValue : '',
                        })
                    );
                    await saveModal(RuleAndEarningModalNames.Earning, _formValue, setIsLoading);
                }
            };

            // Expose the submit handler via ref so other tabs/buttons can trigger the same save logic
            if (!formStateRef.current) {
                formStateRef.current = {};
            }
            formStateRef.current.handleSubmit = handleSubmit;

            const editable = useCallback(
                (inputName: string) => {
                    let result: boolean = false;
                    if (!editMode) {
                        result = true;
                    } else if (editMode && formValue[inputName].editable) {
                        result = true;
                    }
                    return result;
                },
                [editMode, formValue]
            );

            const FilteredOptionalSettings = useMemo(() => {
                const list = PageObjects.Earning.optionalSettingObjects.filter((x: any) =>
                    x.show.includes(`${formValue.applicationScopeType.value}`)
                );

                const sortedList = list
                    .map((x: any) => ({ ...x, name: t(x.name) }))
                    .sort((a: any, b: any) => a.name.localeCompare(b.name));

                return sortedList;
            }, [formValue.applicationScopeType.value]);

            const getValueLabel = () => {
                let label: string = '';
                switch (formValue.calculationDiscountType.value) {
                    case EarningCalculationDiscountType.FixedPrice:
                        label = t('campaignManagement.save.step.rulesAndEarnings.earning.form.newPriceLabel');
                        break;
                    case EarningCalculationDiscountType.Percentage:
                        label = t('campaignManagement.save.step.rulesAndEarnings.earning.form.percentageLabel');
                        break;
                    case EarningCalculationDiscountType.Promotion:
                    case EarningCalculationDiscountType.StaticDiscount:
                        label = t('campaignManagement.save.step.rulesAndEarnings.earning.form.discountValue');
                        break;
                }

                return label;
            };

            return (
                <>
                    <DialogContent dividers>
                        <Grid container>
                            <Grid
                                style={{ paddingLeft: 0, margin: '8px' }}
                                size={{
                                    xs: 12,
                                    sm: 12,
                                    md: 12,
                                    lg: 12,
                                }}
                            >
                                <FormControl fullWidth>
                                    <TextField
                                        required
                                        id={PageObjects.Earning.formInput.Name}
                                        name={PageObjects.Earning.formInput.Name}
                                        label={t(
                                            'campaignManagement.save.step.rulesAndEarnings.earning.form.earningName'
                                        )}
                                        defaultValue={formValue.name.value}
                                        disabled={!editable(PageObjects.Earning.formInput.Name)}
                                        onChange={handleChange}
                                        error={errors.name != undefined || uniqueEarningNameError}
                                        helperText={errors.uniqueEarning || errors.name}
                                    />
                                </FormControl>
                            </Grid>

                            {visible(EarningFormInput.CampaignFilterRuleId) && (
                                <Grid
                                    style={{ paddingLeft: 0, margin: '8px' }}
                                    size={{
                                        xs: 12,
                                        sm: 12,
                                        md: 12,
                                        lg: 12,
                                    }}
                                >
                                    <FormControl fullWidth>
                                        <TextField
                                            select
                                            required
                                            id={PageObjects.Earning.formInput.CampaignFilterRuleId}
                                            name={PageObjects.Earning.formInput.CampaignFilterRuleId}
                                            label={t(
                                                'campaignManagement.save.step.rulesAndEarnings.earning.form.dependentRule'
                                            )}
                                            slotProps={{
                                                select: {
                                                    label: '',
                                                    multiple: false,
                                                    value: formValue.campaignFilterRuleId.value,
                                                    disabled: !editable(
                                                        PageObjects.Earning.formInput.CampaignFilterRuleId
                                                    ),
                                                },
                                            }}
                                            onChange={handleChange}
                                            error={errors.campaignFilterRuleId != undefined}
                                            helperText={errors.campaignFilterRuleId}
                                        >
                                            {rules.length > 0 &&
                                                rules.map((item: any) => {
                                                    return (
                                                        <MenuItem value={item.id} key={item.id}>
                                                            {item.name}
                                                        </MenuItem>
                                                    );
                                                })}
                                        </TextField>
                                    </FormControl>
                                </Grid>
                            )}

                            {visible(EarningFormInput.ApplicationScopeType) && (
                                <Grid
                                    style={{ paddingLeft: 0, margin: '8px' }}
                                    size={{
                                        xs: 12,
                                        sm: 12,
                                        md: 12,
                                        lg: 12,
                                    }}
                                >
                                    <FormControl fullWidth>
                                        <TextField
                                            select
                                            required
                                            id={PageObjects.Earning.formInput.ApplicationScopeType}
                                            name={PageObjects.Earning.formInput.ApplicationScopeType}
                                            label={t(
                                                'campaignManagement.save.step.rulesAndEarnings.earning.form.earningType'
                                            )}
                                            slotProps={{
                                                select: {
                                                    label: '',
                                                    multiple: false,
                                                    value: formValue.applicationScopeType.value,
                                                    disabled: !editable(
                                                        PageObjects.Earning.formInput.ApplicationScopeType
                                                    ),
                                                },
                                            }}
                                            onChange={handleChange}
                                            error={errors.applicationScopeType != undefined}
                                            helperText={errors.applicationScopeType}
                                        >
                                            {PageObjects.Earning.applicationScopeTypeObjects.length > 0 &&
                                                PageObjects.Earning.applicationScopeTypeObjects.map((item: any) => {
                                                    let option: any = item.parent ? (
                                                        <ListSubheader
                                                            style={{
                                                                fontSize: '1.6rem',
                                                                color: 'darkgray',
                                                            }}
                                                        >
                                                            {t(item.name)}
                                                        </ListSubheader>
                                                    ) : (
                                                        <MenuItem
                                                            value={item.id}
                                                            key={item.id}
                                                            style={{
                                                                paddingLeft: item.child ? '32px' : '16px',
                                                            }}
                                                        >
                                                            {t(item.name)}
                                                        </MenuItem>
                                                    );
                                                    return option;
                                                })}
                                        </TextField>
                                    </FormControl>
                                </Grid>
                            )}

                            {visible(EarningFormInput.NumberOfItemPurchasedNthProduct) && (
                                <Grid
                                    style={{ padding: '0 8px', margin: '8px 0' }}
                                    size={{
                                        xs: 12,
                                        sm: 12,
                                        md: 12,
                                        lg: 12,
                                    }}
                                >
                                    <FormControl fullWidth>
                                        <TextField
                                            required
                                            id={PageObjects.Earning.formInput.NumberOfItemPurchasedNthProduct}
                                            name={PageObjects.Earning.formInput.NumberOfItemPurchasedNthProduct}
                                            label={t(
                                                'campaignManagement.save.step.rulesAndEarnings.earning.form.numberOfItemPurchasedNthProduct'
                                            )}
                                            type="number"
                                            defaultValue={formValue.numberOfItemPurchasedNthProduct.value ?? null}
                                            disabled={
                                                !editable(PageObjects.Earning.formInput.NumberOfItemPurchasedNthProduct)
                                            }
                                            onChange={handleChange}
                                            error={errors.numberOfItemPurchasedNthProduct != undefined}
                                            helperText={errors.numberOfItemPurchasedNthProduct}
                                        />
                                    </FormControl>
                                </Grid>
                            )}

                            {/*beraber*/}

                            {visible(EarningFormInput.NumberOfItemPurchased) && (
                                <Grid
                                    style={{ padding: '0 8px', margin: '8px 0' }}
                                    size={{
                                        xs: 6,
                                        sm: 6,
                                        md: 6,
                                        lg: 6,
                                    }}
                                >
                                    <FormControl fullWidth>
                                        <TextField
                                            required
                                            id={PageObjects.Earning.formInput.NumberOfItemPurchased}
                                            name={PageObjects.Earning.formInput.NumberOfItemPurchased}
                                            label={t(
                                                'campaignManagement.save.step.rulesAndEarnings.earning.form.numberOfItemPurchased'
                                            )}
                                            type="number"
                                            defaultValue={formValue.numberOfItemPurchased.value ?? null}
                                            disabled={!editable(PageObjects.Earning.formInput.NumberOfItemPurchased)}
                                            onChange={handleChange}
                                            error={errors.numberOfItemPurchased != undefined}
                                            helperText={errors.numberOfItemPurchased}
                                        />
                                    </FormControl>
                                </Grid>
                            )}

                            {visible(EarningFormInput.NumberOfFreeItems) && (
                                <Grid
                                    style={{ padding: '0 8px', margin: '8px 0' }}
                                    size={{
                                        xs: 6,
                                        sm: 6,
                                        md: 6,
                                        lg: 6,
                                    }}
                                >
                                    <FormControl fullWidth>
                                        <TextField
                                            required
                                            id={PageObjects.Earning.formInput.NumberOfFreeItems}
                                            name={PageObjects.Earning.formInput.NumberOfFreeItems}
                                            label={t(
                                                'campaignManagement.save.step.rulesAndEarnings.earning.form.numberOfDiscountedItem'
                                            )}
                                            type="number"
                                            defaultValue={formValue.numberOfFreeItems.value ?? null}
                                            disabled={!editable(PageObjects.Earning.formInput.NumberOfFreeItems)}
                                            onChange={handleChange}
                                            error={errors.numberOfFreeItems != undefined}
                                            helperText={errors.numberOfFreeItems}
                                        />
                                    </FormControl>
                                </Grid>
                            )}

                            {/*beraber*/}

                            {visible(EarningFormInput.CalculationDiscountType) && (
                                <Grid
                                    style={{ paddingLeft: 0, margin: '8px' }}
                                    size={{
                                        xs: 12,
                                        sm: 12,
                                        md: 12,
                                        lg: 12,
                                    }}
                                >
                                    <FormControl fullWidth error={errors.calculationDiscountType != undefined}>
                                        <InputLabel id="discountType-label">
                                            {t(
                                                'campaignManagement.save.step.rulesAndEarnings.earning.form.discountType'
                                            ) + ' *'}
                                        </InputLabel>
                                        <Select
                                            required
                                            labelId="discountType-label"
                                            label={t(
                                                'campaignManagement.save.step.rulesAndEarnings.earning.form.discountType'
                                            )}
                                            id={PageObjects.Earning.formInput.CalculationDiscountType}
                                            name={PageObjects.Earning.formInput.CalculationDiscountType}
                                            value={formValue.calculationDiscountType.value}
                                            onChange={handleChange}
                                            disabled={!editable(PageObjects.Earning.formInput.CalculationDiscountType)}
                                        >
                                            {PageObjects.Earning.calculationDiscountTypeObjects.length > 0 &&
                                                PageObjects.Earning.calculationDiscountTypeObjects.map((item: any) => (
                                                    <MenuItem key={item.id} value={item.id}>
                                                        {t(item.name)}
                                                    </MenuItem>
                                                ))}
                                        </Select>
                                        {errors.calculationDiscountType && (
                                            <FormHelperText>{errors.calculationDiscountType}</FormHelperText>
                                        )}
                                    </FormControl>
                                </Grid>
                            )}

                            {visible(EarningFormInput.Value) && (
                                <Grid
                                    style={{ padding: '0 8px', margin: '8px 0' }}
                                    size={{
                                        xs: 12,
                                        sm: 12,
                                        md: 12,
                                        lg: 12,
                                    }}
                                >
                                    <FormControl fullWidth>
                                        <TextField
                                            required
                                            id={PageObjects.Earning.formInput.Value}
                                            name={PageObjects.Earning.formInput.Value}
                                            label={getValueLabel()}
                                            value={formValue?.value?.value ?? null}
                                            type="number"
                                            defaultValue={formValue.value.value ?? null}
                                            disabled={!editable(PageObjects.Earning.formInput.Value)}
                                            onChange={handleChange}
                                            slotProps={{
                                                input: {
                                                    inputProps: { min: 1, step: 1 },
                                                },
                                            }}
                                            error={errors.value != undefined}
                                            helperText={errors.value}
                                        />
                                    </FormControl>
                                </Grid>
                            )}

                            <Divider style={{ width: '-webkit-fill-available', margin: '8px 8px' }} />
                            {visible(EarningFormInput.OptionalSetting) && (
                                <Grid
                                    style={{ paddingLeft: 0, margin: '8px' }}
                                    size={{
                                        xs: 12,
                                        sm: 12,
                                        md: 12,
                                        lg: 12,
                                    }}
                                >
                                    <FormControl fullWidth>
                                        <InputLabel id="optionalSetting-label">
                                            {t(
                                                'campaignManagement.save.step.rulesAndEarnings.earning.form.optionalSettings.title'
                                            )}
                                        </InputLabel>
                                        <Select
                                            required
                                            labelId="optionalSetting-label"
                                            label={t(
                                                'campaignManagement.save.step.rulesAndEarnings.earning.form.optionalSettings.title'
                                            )}
                                            id={PageObjects.Earning.formInput.OptionalSetting}
                                            name={PageObjects.Earning.formInput.OptionalSetting}
                                            multiple
                                            value={formValue.optionalSetting.value ?? []}
                                            onChange={handleChangeOptionalSetting}
                                            input={
                                                <OutlinedInput
                                                    label={t(
                                                        'campaignManagement.save.step.rulesAndEarnings.earning.form.optionalSettings.title'
                                                    )}
                                                />
                                            }
                                            renderValue={(selected) =>
                                                PageObjects.Earning.optionalSettingObjects
                                                    .filter((x: any) => selected.includes(x.id))
                                                    .map((x: any) => t(x.name))
                                                    .sort((a: any, b: any) => a.localeCompare(b))
                                                    .join(', ')
                                            }
                                            disabled={!editable(PageObjects.Earning.formInput.OptionalSetting)}
                                        >
                                            {FilteredOptionalSettings.length > 0 &&
                                                FilteredOptionalSettings.map((item: any) => (
                                                    <MenuItem key={item.id} value={item.id}>
                                                        <Checkbox
                                                            checked={formValue.optionalSetting.value.some(
                                                                (x: any) => x == item.id
                                                            )}
                                                        />
                                                        <ListItemText primary={t(item.name)} />
                                                    </MenuItem>
                                                ))}
                                        </Select>
                                    </FormControl>
                                </Grid>
                            )}

                            {visible(EarningFormInput.MaxDiscountAmount) && (
                                <Grid
                                    style={{ paddingLeft: 0, margin: '8px' }}
                                    size={{
                                        xs: 12,
                                        sm: 12,
                                        md: 12,
                                        lg: 12,
                                    }}
                                >
                                    <div
                                        style={{
                                            display: 'flex',
                                            justifyContent: 'space-between',
                                            alignItems: 'center',
                                            border: '1px solid #c4c4c4',
                                            padding: '8px',
                                            borderRadius: '4px',
                                        }}
                                    >
                                        <FormControl fullWidth>
                                            <TextField
                                                required
                                                id={PageObjects.Earning.formInput.MaxDiscountAmount}
                                                name={PageObjects.Earning.formInput.MaxDiscountAmount}
                                                label={t(
                                                    'campaignManagement.save.step.rulesAndEarnings.earning.form.optionalSettings.maxDiscountAmount'
                                                )}
                                                type="number"
                                                defaultValue={formValue.maxDiscountAmount.value}
                                                disabled={!editable(PageObjects.Earning.formInput.MaxDiscountAmount)}
                                                onChange={handleChange}
                                                error={errors.maxDiscountAmount != undefined}
                                                helperText={errors.maxDiscountAmount}
                                            />
                                        </FormControl>
                                        <IconButton
                                            onClick={() =>
                                                handleDeleteOptionalSetting(EarningOptionalSetting.MaxDiscountAmount)
                                            }
                                            disabled={!editable(PageObjects.Earning.formInput.MaxDiscountAmount)}
                                            style={{ margin: '0 4px' }}
                                        >
                                            <Delete style={{ width: '24px', height: '24px' }} />
                                        </IconButton>
                                    </div>
                                </Grid>
                            )}

                            {visible(EarningFormInput.IncludeShippingPriceToGrandTotal) && (
                                <Grid
                                    style={{ paddingLeft: 0, margin: '8px' }}
                                    size={{
                                        xs: 12,
                                        sm: 12,
                                        md: 12,
                                        lg: 12,
                                    }}
                                >
                                    <div
                                        style={{
                                            display: 'flex',
                                            justifyContent: 'space-between',
                                            alignItems: 'center',
                                            border: '1px solid #c4c4c4',
                                            padding: '8px',
                                            borderRadius: '4px',
                                        }}
                                    >
                                        <Stack direction="row" spacing={1} alignItems="center">
                                            <Typography>
                                                {t(
                                                    'campaignManagement.save.step.rulesAndEarnings.earning.form.optionalSettings.includeShippingPrice'
                                                )}
                                            </Typography>
                                            <Switch
                                                id={PageObjects.Earning.formInput.IncludeShippingPriceToGrandTotal}
                                                name={PageObjects.Earning.formInput.IncludeShippingPriceToGrandTotal}
                                                checked={
                                                    formValue.includeShippingPriceToGrandTotal.value ==
                                                    EarningValue.True
                                                }
                                                onChange={handleChange}
                                                disabled={
                                                    !editable(
                                                        PageObjects.Earning.formInput.IncludeShippingPriceToGrandTotal
                                                    )
                                                }
                                            />
                                        </Stack>
                                        <IconButton
                                            onClick={() =>
                                                handleDeleteOptionalSetting(
                                                    EarningOptionalSetting.IncludeShippingPriceToGrandTotal
                                                )
                                            }
                                            style={{ margin: '0 4px' }}
                                            disabled={
                                                !editable(
                                                    PageObjects.Earning.formInput.IncludeShippingPriceToGrandTotal
                                                )
                                            }
                                        >
                                            <Delete style={{ width: '24px', height: '24px' }} />
                                        </IconButton>
                                    </div>
                                </Grid>
                            )}

                            {visible(EarningFormInput.UseDiscountedPrice) && (
                                <Grid
                                    style={{ paddingLeft: 0, margin: '8px' }}
                                    size={{
                                        xs: 12,
                                        sm: 12,
                                        md: 12,
                                        lg: 12,
                                    }}
                                >
                                    <div
                                        style={{
                                            display: 'flex',
                                            justifyContent: 'space-between',
                                            alignItems: 'center',
                                            border: '1px solid #c4c4c4',
                                            padding: '8px',
                                            borderRadius: '4px',
                                        }}
                                    >
                                        <FormControl fullWidth>
                                            <InputLabel id="basePrice-label">
                                                {t(
                                                    'campaignManagement.save.step.rulesAndEarnings.earning.form.optionalSettings.basePrice'
                                                )}
                                            </InputLabel>
                                            <Select
                                                labelId="basePrice-label"
                                                label={t(
                                                    'campaignManagement.save.step.rulesAndEarnings.earning.form.optionalSettings.basePrice'
                                                )}
                                                id={PageObjects.Earning.formInput.UseDiscountedPrice}
                                                name={PageObjects.Earning.formInput.UseDiscountedPrice}
                                                value={
                                                    formValue.useDiscountedPrice.value
                                                        ? EarningValue.True
                                                        : EarningValue.False
                                                }
                                                onChange={handleChange}
                                                disabled={!editable(PageObjects.Earning.formInput.UseDiscountedPrice)}
                                            >
                                                {PageObjects.Earning.BasePriceObjects.length > 0 &&
                                                    PageObjects.Earning.BasePriceObjects.map((item: any) => (
                                                        <MenuItem key={item.id} value={item.id}>
                                                            {t(item.name)}
                                                        </MenuItem>
                                                    ))}
                                            </Select>
                                        </FormControl>
                                        <IconButton
                                            onClick={() =>
                                                handleDeleteOptionalSetting(EarningOptionalSetting.UseDiscountedPrice)
                                            }
                                            style={{ margin: '0 4px' }}
                                            disabled={!editable(PageObjects.Earning.formInput.UseDiscountedPrice)}
                                        >
                                            <Delete style={{ width: '24px', height: '24px' }} />
                                        </IconButton>
                                    </div>
                                </Grid>
                            )}

                            {visible(EarningFormInput.OnlyNonDiscountedProducts) && (
                                <Grid
                                    style={{ paddingLeft: 0, margin: '8px' }}
                                    size={{
                                        xs: 12,
                                        sm: 12,
                                        md: 12,
                                        lg: 12,
                                    }}
                                >
                                    <div
                                        style={{
                                            display: 'flex',
                                            justifyContent: 'space-between',
                                            alignItems: 'center',
                                            border: '1px solid #c4c4c4',
                                            padding: '8px',
                                            borderRadius: '4px',
                                        }}
                                    >
                                        <Stack direction="row" spacing={1} alignItems="center">
                                            <Typography>
                                                {t(
                                                    'campaignManagement.save.step.rulesAndEarnings.earning.form.optionalSettings.onlyNonDiscountedProducts'
                                                )}
                                            </Typography>
                                            <Switch
                                                id={PageObjects.Earning.formInput.OnlyNonDiscountedProducts}
                                                name={PageObjects.Earning.formInput.OnlyNonDiscountedProducts}
                                                checked={formValue.onlyNonDiscountedProducts.value == EarningValue.True}
                                                onChange={handleChange}
                                                disabled={
                                                    !editable(PageObjects.Earning.formInput.OnlyNonDiscountedProducts)
                                                }
                                            />
                                        </Stack>
                                        <IconButton
                                            onClick={() =>
                                                handleDeleteOptionalSetting(
                                                    EarningOptionalSetting.OnlyNonDiscountedProducts
                                                )
                                            }
                                            style={{ margin: '0 4px' }}
                                            disabled={
                                                !editable(PageObjects.Earning.formInput.OnlyNonDiscountedProducts)
                                            }
                                        >
                                            <Delete style={{ width: '24px', height: '24px' }} />
                                        </IconButton>
                                    </div>
                                </Grid>
                            )}

                            {visible(EarningFormInput.PriceSortingType) && (
                                <Grid
                                    style={{ paddingLeft: 0, margin: '8px' }}
                                    size={{
                                        xs: 12,
                                        sm: 12,
                                        md: 12,
                                        lg: 12,
                                    }}
                                >
                                    <div
                                        style={{
                                            display: 'flex',
                                            justifyContent: 'space-between',
                                            alignItems: 'center',
                                            border: '1px solid #c4c4c4',
                                            padding: '8px',
                                            borderRadius: '4px',
                                        }}
                                    >
                                        <FormControl fullWidth>
                                            <InputLabel id="priceSortingType-label">
                                                {t(
                                                    'campaignManagement.save.step.rulesAndEarnings.earning.form.optionalSettings.productSorting'
                                                )}
                                            </InputLabel>
                                            <Select
                                                labelId="priceSortingType-label"
                                                label={t(
                                                    'campaignManagement.save.step.rulesAndEarnings.earning.form.optionalSettings.productSorting'
                                                )}
                                                id={PageObjects.Earning.formInput.PriceSortingType}
                                                name={PageObjects.Earning.formInput.PriceSortingType}
                                                value={
                                                    formValue.priceSortingType.value
                                                        ? EarningValue.True
                                                        : EarningValue.False
                                                }
                                                onChange={handleChange}
                                                disabled={!editable(PageObjects.Earning.formInput.PriceSortingType)}
                                            >
                                                {PageObjects.Earning.PriceSortingTypeObjects.length > 0 &&
                                                    PageObjects.Earning.PriceSortingTypeObjects.map((item: any) => (
                                                        <MenuItem key={item.id} value={item.id}>
                                                            {t(item.name)}
                                                        </MenuItem>
                                                    ))}
                                            </Select>
                                        </FormControl>
                                        <IconButton
                                            onClick={() =>
                                                handleDeleteOptionalSetting(EarningOptionalSetting.PriceSortingType)
                                            }
                                            style={{ margin: '0 4px' }}
                                            disabled={!editable(PageObjects.Earning.formInput.PriceSortingType)}
                                        >
                                            <Delete style={{ width: '24px', height: '24px' }} />
                                        </IconButton>
                                    </div>
                                </Grid>
                            )}

                            {visible(EarningFormInput.MaxDiscountedProductCount) && (
                                <Grid
                                    style={{ paddingLeft: 0, margin: '8px' }}
                                    size={{
                                        xs: 12,
                                        sm: 12,
                                        md: 12,
                                        lg: 12,
                                    }}
                                >
                                    <div
                                        style={{
                                            display: 'flex',
                                            justifyContent: 'space-between',
                                            alignItems: 'center',
                                            border: '1px solid #c4c4c4',
                                            padding: '8px',
                                            borderRadius: '4px',
                                        }}
                                    >
                                        <FormControl fullWidth>
                                            <TextField
                                                required
                                                id={PageObjects.Earning.formInput.MaxDiscountedProductCount}
                                                name={PageObjects.Earning.formInput.MaxDiscountedProductCount}
                                                label={t(
                                                    'campaignManagement.save.step.rulesAndEarnings.earning.form.optionalSettings.maxDiscountedProductCount'
                                                )}
                                                type="number"
                                                defaultValue={formValue.maxDiscountedProductCount.value}
                                                disabled={
                                                    !editable(PageObjects.Earning.formInput.MaxDiscountedProductCount)
                                                }
                                                onChange={handleChange}
                                                slotProps={{
                                                    input: {
                                                        inputProps: { min: 1, max: 999999999999999, step: 1 },
                                                    },
                                                }}
                                                error={errors.maxDiscountedProductCount != undefined}
                                                helperText={errors.maxDiscountedProductCount}
                                            />
                                        </FormControl>
                                        <IconButton
                                            onClick={() =>
                                                handleDeleteOptionalSetting(
                                                    EarningOptionalSetting.MaxDiscountedProductCount
                                                )
                                            }
                                            style={{ margin: '0 4px' }}
                                            disabled={
                                                !editable(PageObjects.Earning.formInput.MaxDiscountedProductCount)
                                            }
                                        >
                                            <Delete style={{ width: '24px', height: '24px' }} />
                                        </IconButton>
                                    </div>
                                </Grid>
                            )}

                            {visible(EarningFormInput.DistributionType) && (
                                <Grid
                                    style={{ paddingLeft: 0, margin: '8px' }}
                                    size={{
                                        xs: 12,
                                        sm: 12,
                                        md: 12,
                                        lg: 12,
                                    }}
                                >
                                    <div
                                        style={{
                                            display: 'flex',
                                            justifyContent: 'space-between',
                                            alignItems: 'center',
                                            border: '1px solid #c4c4c4',
                                            padding: '8px',
                                            borderRadius: '4px',
                                        }}
                                    >
                                        <FormControl fullWidth>
                                            <InputLabel id="distributionType-label">
                                                {t(
                                                    'campaignManagement.save.step.rulesAndEarnings.earning.form.optionalSettings.discountDistributionType'
                                                )}
                                            </InputLabel>
                                            <Select
                                                labelId="distributionType-label"
                                                label={t(
                                                    'campaignManagement.save.step.rulesAndEarnings.earning.form.optionalSettings.discountDistributionType'
                                                )}
                                                id={PageObjects.Earning.formInput.DistributionType}
                                                name={PageObjects.Earning.formInput.DistributionType}
                                                value={formValue.distributionType.value}
                                                onChange={handleChange}
                                                disabled={!editable(PageObjects.Earning.formInput.DistributionType)}
                                            >
                                                {PageObjects.Earning.DistributionTypeObjects.length > 0 &&
                                                    PageObjects.Earning.DistributionTypeObjects.map((item: any) => (
                                                        <MenuItem key={item.id} value={item.id}>
                                                            {t(item.name)}
                                                        </MenuItem>
                                                    ))}
                                            </Select>
                                        </FormControl>
                                        <IconButton
                                            onClick={() =>
                                                handleDeleteOptionalSetting(EarningOptionalSetting.DistributionType)
                                            }
                                            style={{ margin: '0 4px' }}
                                            disabled={!editable(PageObjects.Earning.formInput.DistributionType)}
                                        >
                                            <Delete style={{ width: '24px', height: '24px' }} />
                                        </IconButton>
                                    </div>
                                </Grid>
                            )}

                            {visible(EarningFormInput.ShowDiscountedPrice) && (
                                <Grid
                                    style={{ paddingLeft: 0, margin: '8px' }}
                                    size={{
                                        xs: 12,
                                        sm: 12,
                                        md: 12,
                                        lg: 12,
                                    }}
                                >
                                    <div
                                        style={{
                                            display: 'flex',
                                            justifyContent: 'space-between',
                                            alignItems: 'center',
                                            border: '1px solid #c4c4c4',
                                            padding: '8px',
                                            borderRadius: '4px',
                                        }}
                                    >
                                        <Stack direction="row" spacing={1} alignItems="center">
                                            <Typography>
                                                {t(
                                                    'campaignManagement.save.step.rulesAndEarnings.earning.form.optionalSettings.showDiscountedPrice'
                                                )}
                                            </Typography>
                                            <Switch
                                                id={PageObjects.Earning.formInput.ShowDiscountedPrice}
                                                name={PageObjects.Earning.formInput.ShowDiscountedPrice}
                                                checked={formValue.showDiscountedPrice.value == EarningValue.True}
                                                onChange={handleChange}
                                                disabled={!editable(PageObjects.Earning.formInput.ShowDiscountedPrice)}
                                            />
                                        </Stack>
                                        <IconButton
                                            onClick={() =>
                                                handleDeleteOptionalSetting(EarningOptionalSetting.ShowDiscountedPrice)
                                            }
                                            style={{ margin: '0 4px' }}
                                            disabled={!editable(PageObjects.Earning.formInput.ShowDiscountedPrice)}
                                        >
                                            <Delete style={{ width: '24px', height: '24px' }} />
                                        </IconButton>
                                    </div>
                                </Grid>
                            )}
                        </Grid>
                    </DialogContent>
                    <DialogActions>
                        <Stack direction="row">
                            <Item style={{ padding: '16px 10px 16px 16px' }}>
                                <SCUIButton
                                    text={t(
                                        'campaignManagement.save.step.rulesAndEarnings.earning.form.action.cancelButton'
                                    )}
                                    type="secondary"
                                    sizeType="icon"
                                    size={'s'}
                                    onClick={() => closeModal(name)}
                                />
                            </Item>
                            <Item style={{ padding: '16px 16px 16px 10px' }}>
                                <SCUIButton
                                    autoFocusButton={true}
                                    text={t(
                                        'campaignManagement.save.step.rulesAndEarnings.earning.form.action.saveButton'
                                    )}
                                    type="primary"
                                    sizeType="fixed"
                                    disabled={uniqueEarningNameError || isLoading}
                                    size={'s'}
                                    onClick={() => handleSubmit(manualEvent)}
                                />
                            </Item>
                        </Stack>
                    </DialogActions>
                </>
            );
        };

        // Image Button component for badge selection
        const BadgeImageButton = ({
            languageCode,
            platform,
            label,
        }: {
            languageCode: string;
            platform: string;
            label: string;
        }) => {
            return (
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                    <Typography variant="caption" sx={{ textAlign: 'center', mb: 0.5 }}>
                        {label}
                    </Typography>
                    <Button
                        onClick={() => openBadgeGallery(languageCode, platform)}
                        sx={{
                            width: '141px',
                            height: '95px',
                            border: '1px dashed #c8c8c8',
                            borderRadius: '15px',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            backgroundColor: '#ffffff',
                            '&:hover': {
                                backgroundColor: 'rgba(0, 0, 0, 0.04)',
                            },
                        }}
                    >
                        <FileUpload sx={{ width: 20, height: 20, color: '#626262' }} />
                        <Typography
                            component="span"
                            sx={{ fontSize: '10px', padding: '8px', paddingBottom: 0, color: '#626262' }}
                        >
                            {t('campaignManagement.save.step.websiteAppearance.badge.item.buttonPlaceHolder')}
                        </Typography>
                    </Button>
                </Box>
            );
        };

        // Image with overlay component for showing selected badges
        // Uses internal hover state to prevent re-rendering the entire modal
        const BadgeImageWithOverlay = ({
            languageCode,
            platform,
            imageUrl,
            label,
        }: {
            languageCode: string;
            platform: string;
            imageUrl: string;
            label: string;
        }) => {
            // Manage hover state internally to prevent parent re-renders
            const [isHovered, setIsHovered] = useState(false);

            return (
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                    <Typography variant="caption" sx={{ textAlign: 'center', mb: 0.5 }}>
                        {label}
                    </Typography>
                    <Box
                        sx={{
                            position: 'relative',
                            width: '141px',
                            height: '95px',
                            display: 'inline-block',
                            overflow: 'hidden',
                            borderRadius: '5px',
                            border: '1px solid #c8c8c8',
                        }}
                        onMouseEnter={() => setIsHovered(true)}
                        onMouseLeave={() => setIsHovered(false)}
                    >
                        <img
                            src={imageUrl}
                            alt={label}
                            style={{
                                width: '100%',
                                height: '100%',
                                objectFit: 'scale-down',
                            }}
                        />
                        {isHovered && (
                            <Box
                                sx={{
                                    position: 'absolute',
                                    top: 0,
                                    left: 0,
                                    width: '100%',
                                    height: '100%',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    justifyContent: 'center',
                                    alignItems: 'center',
                                    backgroundColor: 'rgba(0, 0, 0, 0.5)',
                                    zIndex: 999,
                                }}
                            >
                                <Box sx={{ textAlign: 'center' }}>
                                    <IconButton
                                        size="small"
                                        sx={{ backgroundColor: 'white', margin: '3px' }}
                                        onClick={() => openBadgeGallery(languageCode, platform)}
                                    >
                                        <EditTwoTone sx={{ fontSize: '16px', color: 'black' }} />
                                    </IconButton>
                                    <IconButton
                                        size="small"
                                        sx={{ backgroundColor: 'white', margin: '3px' }}
                                        onClick={() => deleteBadge(languageCode, platform)}
                                    >
                                        <DeleteOutlineTwoTone sx={{ fontSize: '16px', color: 'red' }} />
                                    </IconButton>
                                </Box>
                            </Box>
                        )}
                    </Box>
                </Box>
            );
        };

        const WebsiteAppearanceTab = () => {
            const assetTargets = [
                { key: 'web', label: t('campaignManagement.save.step.websiteAppearance.badge.item.website') },
                { key: 'mobileWeb', label: t('campaignManagement.save.step.websiteAppearance.badge.item.responsive') },
                { key: 'mobileApp', label: t('campaignManagement.save.step.websiteAppearance.badge.item.application') },
            ];

            return (
                <Box sx={{ p: 2 }}>
                    {/* Campaign Messages Section */}
                    <Typography variant="h6" sx={{ mb: 2 }}>
                        {t(
                            'campaignManagement.save.step.rulesAndEarnings.earning.modal.websiteAppearanceTab.campaignMessageTitle'
                        )}
                    </Typography>
                    {languages.map((language: any) => (
                        <CampaignMessageInput
                            key={language.id}
                            languageId={language.id}
                            languageName={language.name}
                            initialValue={campaignMessages[language.id] || ''}
                            onBlurUpdate={handleCampaignMessageChange}
                        />
                    ))}

                    <Divider sx={{ my: 3 }} />

                    {/* Badges Section */}
                    <Typography variant="h6" sx={{ mb: 1 }}>
                        {t(
                            'campaignManagement.save.step.rulesAndEarnings.earning.modal.websiteAppearanceTab.badgeTitle'
                        )}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        {t(
                            'campaignManagement.save.step.rulesAndEarnings.earning.modal.websiteAppearanceTab.badgeDescription'
                        )}
                    </Typography>

                    {languages.map((language: any) => (
                        <Box key={language.short_code} sx={{ mb: 3 }}>
                            <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 'bold' }}>
                                {language.name}
                            </Typography>
                            <Stack
                                direction="row"
                                justifyContent="flex-start"
                                alignItems="flex-start"
                                spacing={3}
                                useFlexGap
                                flexWrap="wrap"
                            >
                                {assetTargets.map((target) => {
                                    const imageUrl =
                                        badgeData.find((badge: any) => badge.culture === language.short_code)?.[
                                            target.key
                                        ] || '';
                                    return imageUrl ? (
                                        <BadgeImageWithOverlay
                                            key={target.key}
                                            languageCode={language.short_code}
                                            platform={target.key}
                                            imageUrl={imageUrl}
                                            label={target.label}
                                        />
                                    ) : (
                                        <BadgeImageButton
                                            key={target.key}
                                            languageCode={language.short_code}
                                            platform={target.key}
                                            label={target.label}
                                        />
                                    );
                                })}
                            </Stack>
                            <Divider sx={{ mt: 2 }} />
                        </Box>
                    ))}

                    {/* Badge Gallery Modal */}
                    <SCUIImageGallery
                        open={isImageGalleryOpen}
                        onClose={closeBadgeGallery}
                        onSelect={handleBadgeSelect}
                        campaignProps={props}
                        initialBadgeTarget={
                            selectedBadgeTarget
                                ? {
                                      culture: selectedBadgeTarget.languageCode,
                                      assetTarget: selectedBadgeTarget.platform,
                                  }
                                : undefined
                        }
                    />
                </Box>
            );
        };

        return (
            <BootstrapDialog
                onClose={() => closeModal(name)}
                aria-labelledby="customized-dialog-title"
                open={open}
                maxWidth={'md'}
                fullWidth
            >
                <DialogTitle sx={{ m: 2, p: 2, pb: 0 }} id="customized-dialog-title" style={{ border: 0 }}>
                    <Typography variant="h6" gutterBottom style={{ fontSize: '2rem' }}>
                        {modalInfo.editMode
                            ? t('campaignManagement.save.step.rulesAndEarnings.earning.modal.edit.title')
                            : t('campaignManagement.save.step.rulesAndEarnings.earning.modal.create.title')}
                    </Typography>
                    <Typography variant="body1" gutterBottom style={{ fontSize: '1.5rem' }}>
                        {modalInfo.editMode
                            ? t('campaignManagement.save.step.rulesAndEarnings.earning.modal.edit.description')
                            : t('campaignManagement.save.step.rulesAndEarnings.earning.modal.create.description')}
                    </Typography>
                </DialogTitle>
                <IconButton
                    aria-label="close"
                    onClick={() => closeModal(name)}
                    sx={{
                        position: 'absolute',
                        right: 8,
                        top: 8,
                        color: (theme) => theme.palette.grey[500],
                    }}
                >
                    <Close />
                </IconButton>

                <Box sx={{ borderBottom: 1, borderColor: 'divider', mx: 2 }}>
                    <Tabs value={activeTab} onChange={handleTabChange} aria-label="earning modal tabs">
                        <Tab
                            label={t('campaignManagement.save.step.rulesAndEarnings.earning.modal.tabs.earningDetails')}
                            id="earning-tab-0"
                            aria-controls="earning-tabpanel-0"
                        />
                        <Tab
                            label={t(
                                'campaignManagement.save.step.rulesAndEarnings.earning.modal.tabs.websiteAppearance'
                            )}
                            id="earning-tab-1"
                            aria-controls="earning-tabpanel-1"
                        />
                    </Tabs>
                </Box>

                <TabPanel value={activeTab} index={0}>
                    <div className="earning-form">
                        <EarningForm formStateRef={earningFormStateRef} />
                    </div>
                </TabPanel>

                <TabPanel value={activeTab} index={1}>
                    <DialogContent dividers sx={{ maxHeight: '60vh', overflow: 'auto' }}>
                        <WebsiteAppearanceTab />
                    </DialogContent>
                    <DialogActions>
                        <Stack direction="row">
                            <Item style={{ padding: '16px 10px 16px 16px' }}>
                                <SCUIButton
                                    text={t(
                                        'campaignManagement.save.step.rulesAndEarnings.earning.form.action.cancelButton'
                                    )}
                                    type="secondary"
                                    sizeType="icon"
                                    size={'s'}
                                    onClick={() => closeModal(name)}
                                />
                            </Item>
                            <Item style={{ padding: '16px 16px 16px 10px' }}>
                                <SCUIButton
                                    autoFocusButton={true}
                                    text={t(
                                        'campaignManagement.save.step.rulesAndEarnings.earning.form.action.saveButton'
                                    )}
                                    type="primary"
                                    sizeType="fixed"
                                    size={'s'}
                                    onClick={() => {
                                        // Trigger the same submit logic used in the first tab
                                        const manualEvent = {
                                            preventDefault: () => {},
                                        } as any;
                                        if (earningFormStateRef.current?.handleSubmit) {
                                            earningFormStateRef.current.handleSubmit(manualEvent);
                                        }
                                    }}
                                />
                            </Item>
                        </Stack>
                    </DialogActions>
                </TabPanel>
            </BootstrapDialog>
        );
    });

    const DraggableEarningItem: FC<any> = ({ earning, index }) => {
        let { id, name, campaignFilterRuleId } = earning;

        const [ruleId, setRuleId] = useState<number | null>(campaignFilterRuleId);
        const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

        const handleMenuOpen = (event: React.MouseEvent<HTMLButtonElement>) => {
            setAnchorEl(event.currentTarget);
        };

        const handleMenuClose = () => {
            setAnchorEl(null);
        };

        return (
            <Draggable draggableId={id.toString()} index={index}>
                {(provided) => (
                    <div {...provided.draggableProps} ref={provided.innerRef}>
                        <Item
                            sx={{
                                mb: 1,
                                p: 2,
                            }}
                            style={{ border: '1px solid #dadada' }}
                        >
                            <Grid container style={{ alignItems: 'center' }}>
                                <Grid
                                    size={{
                                        xs: 1,
                                        sm: 1,
                                        md: 1,
                                        lg: 1,
                                    }}
                                >
                                    <Typography {...provided.dragHandleProps} noWrap>
                                        <DragIndicator sx={{ width: '24px', height: '24px' }} />
                                    </Typography>
                                </Grid>
                                <Grid
                                    size={{
                                        xs: 5,
                                        sm: 5,
                                        md: 5,
                                        lg: 5,
                                    }}
                                >
                                    <Typography noWrap>
                                        <Tooltip title={name}>
                                            {name.length > 35 ? `${name.substring(0, 35)}...` : name}
                                        </Tooltip>
                                    </Typography>
                                </Grid>
                                <Grid
                                    size={{
                                        xs: 4,
                                        sm: 4,
                                        md: 4,
                                        lg: 5,
                                    }}
                                >
                                    <SelectRuleWithConfirmation earningId={id} ruleId={ruleId} setRuleId={setRuleId} />
                                </Grid>
                                <Grid
                                    size={{
                                        xs: 2,
                                        sm: 2,
                                        md: 2,
                                        lg: 1,
                                    }}
                                >
                                    <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                                        <IconButton
                                            aria-controls={anchorEl ? 'basic-menu' : undefined}
                                            aria-haspopup="true"
                                            aria-expanded={anchorEl ? 'true' : undefined}
                                            onClick={handleMenuOpen}
                                        >
                                            <MoreHoriz sx={{ fontSize: 24 }} />
                                        </IconButton>
                                        <Paper sx={{ maxWidth: '100%' }}>
                                            <Menu
                                                id="basic-menu"
                                                anchorEl={anchorEl}
                                                open={anchorEl != null}
                                                onClose={handleMenuClose}
                                                slotProps={{
                                                    list: {
                                                        'aria-labelledby': 'basic-button',
                                                    },
                                                }}
                                                anchorOrigin={{
                                                    vertical: 'top',
                                                    horizontal: 'left',
                                                }}
                                                transformOrigin={{
                                                    vertical: 'top',
                                                    horizontal: 'left',
                                                }}
                                            >
                                                <MenuItem onClick={() => editEarning(id)}>
                                                    <ListItemIcon>
                                                        <Edit fontSize="small" />
                                                    </ListItemIcon>
                                                    <ListItemText>
                                                        {t(
                                                            'campaignManagement.save.step.rulesAndEarnings.earning.item.action.editButton'
                                                        )}
                                                    </ListItemText>
                                                </MenuItem>
                                                <ConfirmationDialogWithTrigger
                                                    triggerComponent={
                                                        <MenuItem>
                                                            <ListItemIcon>
                                                                <Close fontSize="small" />
                                                            </ListItemIcon>
                                                            <ListItemText>
                                                                {t(
                                                                    'campaignManagement.save.step.rulesAndEarnings.earning.item.action.deleteButton'
                                                                )}
                                                            </ListItemText>
                                                        </MenuItem>
                                                    }
                                                    title={t(
                                                        'campaignManagement.save.step.rulesAndEarnings.earning.item.confirmationDialog.title'
                                                    )}
                                                    description={t(
                                                        'campaignManagement.save.step.rulesAndEarnings.earning.item.confirmationDialog.description'
                                                    )}
                                                    onConfirm={() => {
                                                        deleteEarning(id);
                                                    }}
                                                />
                                            </Menu>
                                        </Paper>
                                    </div>
                                </Grid>
                            </Grid>
                        </Item>
                    </div>
                )}
            </Draggable>
        );
    };

    const RuleList = () => {
        const rulesWithFilterTypeCount = useMemo(() => {
            return rules.filter((rule: any) => {
                return (
                    rule.campaignFilters &&
                    rule.campaignFilters.some((filter: any) => filter.campaignFilterTypeId === RuleType.Option)
                );
            }).length;
        }, [rules]);

        const isBulkRuleCreateDisabled = rulesWithFilterTypeCount < 2;

        return (
            <>
                <Paper style={{ marginBottom: '20px' }}>
                    <Typography variant="h6" gutterBottom style={{ fontSize: '1.8rem' }}>
                        {t('campaignManagement.save.step.rulesAndEarnings.rule.title')}
                    </Typography>

                    <SCUIDraggableList onDragEnd={onDragEndRule}>
                        {rules.length > 0 &&
                            rules.map((rule: any, index: number) => (
                                <DraggableRuleItem rule={rule} index={index} key={index} />
                            ))}
                    </SCUIDraggableList>

                    {rules.length == 0 && (
                        <p
                            style={{
                                margin: '8px 0',
                                color: '#d32f2f',
                                fontSize: 'small',
                            }}
                        >
                            {t('campaignManagement.save.step.rulesAndEarnings.rule.noRecordMessage')}
                        </p>
                    )}

                    <Box sx={{ display: 'flex', gap: 5, flexDirection: 'row', justifyContent: 'flex-start' }}>
                        <SCUIButton
                            text={t('campaignManagement.save.step.rulesAndEarnings.rule.action.createButton')}
                            type="ghost"
                            buttonType="button"
                            sizeType="default"
                            size={'s'}
                            showStartIcon={true}
                            startIcon={<AddCircle />}
                            onClick={addRule}
                        />

                        <SCUIButton
                            text={t('campaignManagement.save.step.rulesAndEarnings.rule.action.handleBulkRuleCreate')}
                            type="ghost"
                            buttonType="button"
                            sizeType="default"
                            size={'s'}
                            showStartIcon={true}
                            startIcon={<CloudUploadOutlined />}
                            onClick={handleBulkRuleCreate}
                            disabled={isBulkRuleCreateDisabled}
                        />
                    </Box>
                </Paper>
                <StepRulesTimerComponent {...props} modalInfo={modalInfo} rules={rules} getRules={getRules} />
                {(modalInfo.name == RuleAndEarningModalNames.Rule ||
                    modalInfo.name == RuleAndEarningModalNames.RuleCondition) &&
                    modalInfo.open && <RuleModal name={modalInfo.name} open={modalInfo.open} rules={rules} />}
                {modalInfo.name == RuleAndEarningModalNames.RuleBulkCreate && modalInfo.open && (
                    <RuleBulkCreateModal name={modalInfo.name} open={modalInfo.open} rules={rules} />
                )}
            </>
        );
    };

    const EarningList = () => {
        return (
            <>
                <Paper style={{ marginBottom: '20px' }}>
                    <Typography variant="h6" gutterBottom style={{ fontSize: '1.8rem' }}>
                        {t('campaignManagement.save.step.rulesAndEarnings.earning.title')}
                    </Typography>

                    <SCUIDraggableList onDragEnd={onDragEndEarning}>
                        {earnings.length > 0 &&
                            earnings.map((earning: any, index: number) => {
                                return <DraggableEarningItem earning={earning} index={index} key={index} />;
                            })}
                    </SCUIDraggableList>

                    {earnings.length == 0 && (
                        <p
                            style={{
                                margin: '8px 0',
                                color: '#d32f2f',
                                fontSize: 'small',
                            }}
                        >
                            {t('campaignManagement.save.step.rulesAndEarnings.earning.noRecordMessage')}
                        </p>
                    )}

                    <SCUIButton
                        text={t('campaignManagement.save.step.rulesAndEarnings.earning.action.createButton')}
                        type="ghost"
                        buttonType="button"
                        sizeType="default"
                        size={'s'}
                        showStartIcon={true}
                        startIcon={<AddCircle />}
                        onClick={addEarning}
                        disabled={rules.length == 0}
                    />
                </Paper>

                {modalInfo.name == RuleAndEarningModalNames.Earning && modalInfo.open && (
                    <EarningModal name={RuleAndEarningModalNames.Earning} open={modalInfo.open} />
                )}
            </>
        );
    };

    return (
        <Card className="step-form-card">
            <form
                id="StepEarningAndRules"
                name="StepEarningAndRules"
                noValidate={false}
                // onSubmit={stepperOnClickNext}
            >
                <CardHeader
                    className="step-form-card-header"
                    avatar={
                        <Avatar sx={{ bgcolor: blue[50], color: blue['A700'] }} variant="rounded">
                            <LocalAtm sx={{ height: 24, width: 24 }} />
                        </Avatar>
                    }
                    title={t('campaignManagement.save.step.rulesAndEarnings.title')}
                    subheader={t('campaignManagement.save.step.rulesAndEarnings.description')}
                />
                <CardContent className="step-form-card-content">
                    <Grid container spacing={2}>
                        <Grid
                            size={{
                                xs: 6,
                                sm: 6,
                                md: 6,
                                lg: 6,
                            }}
                        >
                            <RuleList />
                        </Grid>
                        <Grid
                            size={{
                                xs: 6,
                                sm: 6,
                                md: 6,
                                lg: 6,
                            }}
                        >
                            <EarningList />
                        </Grid>
                    </Grid>
                </CardContent>
                <CardActions sx={{ m: 1 }} style={{ display: 'flex', justifyContent: 'flex-end' }}>
                    <Stack direction="row">
                        <Item>
                            <SCUIButton
                                text={t('campaignManagement.save.step.action.backButton')}
                                type="secondary"
                                sizeType="icon"
                                size={'s'}
                                buttonType="button"
                                onClick={stepperOnClickReset}
                            />
                        </Item>
                        <Item>
                            <SCUIButton
                                text={t('campaignManagement.save.step.action.saveButton')}
                                type="secondary"
                                sizeType="fixed"
                                size={'s'}
                                buttonType="button"
                                onClick={saveStep}
                                disabled={saveButtonDisabled}
                            />
                        </Item>
                        <Item>
                            <SCUIButton
                                text={t('campaignManagement.save.step.action.saveAndContinueButton')}
                                type="primary"
                                sizeType="fixed"
                                size={'s'}
                                buttonType="button"
                                onClick={saveForm}
                                disabled={saveButtonDisabled}
                            />
                        </Item>
                    </Stack>
                </CardActions>
            </form>
        </Card>
    );
};
